<template>
  <div>
    <!-- Header -->
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Contratos</h1>
      <v-btn color="primary" @click="openCreate">
        <v-icon left>mdi-plus</v-icon>Nuevo contrato
      </v-btn>
    </v-row>

    <!-- Alert -->
    <v-alert v-if="error && !dialog" type="error" dense outlined dismissible class="mb-3">{{ error }}</v-alert>

    <!-- Filtros: misma card de selects que MRR Breakdown. Año y mes no aplican aquí. -->
    <v-card outlined class="mb-4 pa-4">
      <v-row dense align="center">
        <v-col cols="6" sm="4" md="3">
          <v-select
            v-model="filtroPais"
            :items="opcionesPais"
            label="País"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <v-select
            v-model="filtroStatus"
            :items="opcionesEstado"
            label="Estado"
            outlined dense hide-details
          />
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="filtroLinea"
            :items="opcionesLinea"
            label="Línea de negocio"
            outlined dense hide-details
          />
        </v-col>
      </v-row>

      <v-row dense align="center" class="mt-2">
        <v-col cols="12" md="5">
          <v-autocomplete
            v-model="filtroContrato"
            :items="contratosDisponibles"
            item-text="text"
            item-value="value"
            label="Contrato"
            outlined dense hide-details clearable
            prepend-inner-icon="mdi-file-document-outline"
            no-data-text="Sin contratos que coincidan"
          />
        </v-col>
        <v-spacer />
        <v-col cols="12" md="auto">
          <v-btn small text color="primary" @click="limpiarFiltros">
            Limpiar filtros
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tabla -->
    <v-data-table
      :headers="headers"
      :items="filteredContratos"
      :loading="loading"
      :items-per-page="25"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [25, 50, -1] }"
      :custom-sort="sortContratos"
    >
      <!-- Código -->
      <template v-slot:item.codigo="{ item }">
        <span class="font-weight-medium primary--text"
              style="cursor:pointer; text-decoration:underline"
              @click="openView(item)">
          {{ item.codigo }}
        </span>
      </template>

      <!-- Broker -->
      <template v-slot:item.canal="{ item }">
        {{ item.canal && item.canal !== 'Directo' ? item.canal : '—' }}
      </template>

      <!-- Producto -->
      <template v-slot:item.producto="{ item }">
        <span v-if="item.producto">{{ item.producto.nombre }}</span>
        <span v-else-if="item.lineaNegocio === 'B2B Employee Benefit'" class="grey--text text--darken-1">{{ ebProductoNombre }}</span>
        <span v-else>—</span>
      </template>

      <!-- País -->
      <template v-slot:item.pais="{ item }">
        {{ item.company && item.company.country ? item.company.country.name : '—' }}
      </template>

      <!-- Línea de negocio -->
      <template v-slot:item.lineaNegocio="{ item }">
        <v-chip x-small :color="lineaColor(item.lineaNegocio)" dark>{{ item.lineaNegocio }}</v-chip>
        <v-chip v-if="item.isCountryDefault" x-small outlined color="primary" class="ml-1">Default</v-chip>
      </template>

      <!-- Estado -->
      <template v-slot:item.status="{ item }">
        <ContractStatusChip :status="item.status" />
      </template>

      <!-- Vencimiento (naranja si ya venció) -->
      <template v-slot:item.endDate="{ item }">
        <span :class="isExpired(item.endDate) ? 'orange--text text--darken-2 font-weight-medium' : ''">
          {{ item.endDate ? item.endDate.slice(0, 10) : '—' }}
        </span>
      </template>

      <!-- Ejecutivo -->
      <template v-slot:item.executive="{ item }">
        {{ item.executive ? `${item.executive.name} ${item.executive.surname}` : '—' }}
      </template>

      <!-- NPS de la medición más reciente; abre el detalle con el historial -->
      <template v-slot:item.npsSummary="{ item }">
        <ContractNpsChip :summary="item.npsSummary" @open="openNps(item)" />
      </template>

      <!-- Acciones -->
      <template v-slot:item.actions="{ item }">
        <div class="d-inline-flex align-center">
          <ContractDocumentActions
            :contrato="item"
            @updated="onDocumentUpdated"
            @notify="onDocumentNotify"
          />
          <v-btn icon small @click="openEdit(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="pink" @click="openDeleteDialog(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog confirmar eliminar dominio del contrato -->
    <v-dialog v-model="removeDomainDialog" max-width="420" @keydown.esc="removeDomainDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">Quitar dominio</v-card-title>
        <hr class="my-2" />
        <v-card-text>¿Está seguro de que desea quitar este dominio del contrato?</v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="removeDomainDialog = false">Cancelar</v-btn>
          <v-btn color="pink" dark rounded @click="confirmRemoveDomain">Sí, quitar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog confirmar eliminar plan del contrato -->
    <v-dialog v-model="removePlanDialog" max-width="420" @keydown.esc="removePlanDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">Quitar plan</v-card-title>
        <hr class="my-2" />
        <v-card-text>¿Está seguro de que desea quitar este plan del contrato?</v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="removePlanDialog = false">Cancelar</v-btn>
          <v-btn color="pink" dark rounded @click="confirmRemovePlan">Sí, quitar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog confirmar eliminación -->
    <v-dialog v-model="deleteDialog" max-width="480" @keydown.esc="deleteDialog = false">
      <v-card class="pa-2">
        <v-card-title class="text-uppercase">
          Eliminar contrato {{ deleteTarget ? deleteTarget.codigo : '' }}
        </v-card-title>
        <hr class="my-2" />
        <v-card-text>¿Está seguro de que desea eliminar este contrato? Esta acción no se puede deshacer.</v-card-text>
        <hr class="my-2" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="pink" dark rounded :loading="deleting" @click="confirmDelete">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ContractNpsModal
      v-model="npsDialog"
      :contrato="npsItem"
      :line-color="npsItem ? lineaColor(npsItem.lineaNegocio) : undefined"
      @updated="onNpsUpdated"
    />

    <!-- Dialog vista detalle (solo lectura): vigencia arriba y las mismas pestañas
         del formulario de edición, para que "Editar" abra justo lo que se estaba viendo. -->
    <v-dialog v-model="viewDialog" max-width="600" @keydown.esc="viewDialog = false">
      <v-card v-if="viewItem">
        <v-card-title class="pb-3 d-flex align-center">
          <span class="text-h6 font-weight-bold mr-2">{{ viewItem.codigo }}</span>
          <span class="text-body-2 grey--text text--darken-1">{{ (viewItem.company && viewItem.company.name) || '—' }}</span>
          <v-spacer />
          <v-chip x-small :color="lineaColor(viewItem.lineaNegocio)" dark class="mr-1">{{ viewItem.lineaNegocio }}</v-chip>
          <v-chip v-if="viewItem.isCountryDefault" x-small outlined color="primary" class="mr-1">Default del país</v-chip>
          <ContractStatusChip :status="viewItem.status" />
        </v-card-title>

        <div class="px-6">
          <div class="contrato-vigencia">
            <div class="d-flex justify-space-between align-baseline mb-2">
              <span class="text-body-2 font-weight-bold">{{ viewVigencia.title }}</span>
              <span class="text-caption grey--text text--darken-1">{{ viewItem.tipoFacturacion || '—' }}</span>
            </div>
            <div class="contrato-vigencia__track d-flex align-center">
              <span class="contrato-vigencia__dot" />
              <span class="contrato-vigencia__filled" :style="{ flexGrow: viewVigencia.filled }" />
              <span class="contrato-vigencia__today" />
              <span
                class="contrato-vigencia__rest"
                :class="{ 'contrato-vigencia__rest--open': !viewItem.endDate }"
                :style="{ flexGrow: viewVigencia.rest }"
              />
            </div>
            <div class="d-flex justify-space-between text-caption grey--text text--darken-1 mt-1">
              <span>Inicio {{ viewItem.startDate ? viewItem.startDate.slice(0, 10) : '—' }}</span>
              <span>Hoy</span>
              <span :class="isExpired(viewItem.endDate) ? 'orange--text text--darken-2 font-weight-medium' : ''">
                {{ viewItem.endDate ? `Vence ${viewItem.endDate.slice(0, 10)}` : 'Sin vencimiento' }}
              </span>
            </div>
          </div>
        </div>

        <v-tabs v-model="viewTab" class="px-6 mt-3">
          <v-tab v-for="t in viewTabs" :key="t.value" :href="`#${t.value}`">{{ t.text }}</v-tab>
        </v-tabs>
        <v-divider />

        <v-card-text class="contrato-view-panel pt-4 pb-2">

          <template v-if="viewTab === 'general'">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1">Tipo de contrato</div>
              <div class="text-body-2 font-weight-medium">{{ viewTipoContrato }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1">Estado</div>
              <div class="text-body-2"><ContractStatusChip :status="viewItem.status" /></div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Empresa</div>
              <div class="text-body-2 font-weight-medium">{{ (viewItem.company && viewItem.company.name) || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">País</div>
              <div class="text-body-2 font-weight-medium">{{ (viewItem.company && viewItem.company.country && viewItem.company.country.name) || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Línea de negocio</div>
              <div class="text-body-2 font-weight-medium">{{ viewItem.lineaNegocio || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Producto</div>
              <div class="text-body-2 font-weight-medium">{{ (viewItem.producto && viewItem.producto.nombre) || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Moneda</div>
              <div class="text-body-2 font-weight-medium">{{ viewCurrency }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Fecha de inicio</div>
              <div class="text-body-2 font-weight-medium">{{ viewItem.startDate ? viewItem.startDate.slice(0, 10) : '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Fecha de término</div>
              <div class="text-body-2 font-weight-medium" :class="isExpired(viewItem.endDate) ? 'orange--text text--darken-2' : ''">
                {{ viewItem.endDate ? viewItem.endDate.slice(0, 10) : 'Sin vencimiento' }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Canal</div>
              <div class="text-body-2 font-weight-medium">{{ viewItem.canal || '—' }}</div>
            </v-col>
            <v-col v-if="viewItem.hiringModality" cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Modalidad de contratación</div>
              <div class="text-body-2 font-weight-medium">{{ hiringModalityLabel(viewItem.hiringModality) }}</div>
            </v-col>
            <v-col v-if="viewItem.brokerModality" cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Modalidad de broker</div>
              <div class="text-body-2 font-weight-medium">{{ brokerModalityLabel(viewItem.brokerModality) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Ejecutivo</div>
              <div class="text-body-2 font-weight-medium">
                {{ viewItem.executive ? `${viewItem.executive.name} ${viewItem.executive.surname}` : '—' }}
              </div>
            </v-col>
          </v-row>



          <template v-if="viewItem.domains && viewItem.domains.length > 0">
            <div class="section-label d-flex align-center mb-3 mt-4">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Dominios</span>
              <v-divider />
            </div>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-chip
                v-for="(d, idx) in viewItem.domains"
                :key="idx"
                small outlined color="primary"
              >
                <v-icon left x-small>mdi-web</v-icon>{{ d.domain }}
              </v-chip>
            </div>
          </template>
          </template>

          <template v-if="viewTab === 'comercial'">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Tipo de facturación</div>
              <div class="text-body-2 font-weight-medium">{{ viewItem.tipoFacturacion || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Se factura por</div>
              <div class="text-body-2 font-weight-medium">{{ billingBasisLabel(viewItem.billingBasis) }}</div>
            </v-col>
            <v-col v-if="viewItem.netAmount" cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Monto contrato</div>
              <div class="text-body-2 font-weight-medium">{{ viewItem.netAmount.toLocaleString('es-CL') }}</div>
            </v-col>
            <template v-if="!viewIsUnitBilled">
              <v-col v-if="viewItem.membershipQuota" cols="6">
                <div class="text-caption grey--text text--darken-1 mt-2">Membresías contratadas</div>
                <div class="text-body-2 font-weight-medium">{{ viewItem.membershipQuota }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text text--darken-1 mt-2">Límite por colaborador</div>
                <div class="text-body-2 font-weight-medium">{{ viewItem.membershipLimit != null ? viewItem.membershipLimit : '—' }}</div>
              </v-col>
            </template>
          </v-row>

          <template v-if="viewItem.paymentModes && viewItem.paymentModes.length > 0">
            <div class="section-label d-flex align-center mb-3 mt-4">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Descuentos</span>
              <v-divider />
            </div>
            <v-sheet color="purple lighten-5" rounded class="pa-2 d-flex align-center flex-wrap" style="gap: 12px">
              <span v-if="viewItem.paymentModes[0].monthlyDiscount" class="text-caption">Mensual: <strong>{{ viewItem.paymentModes[0].monthlyDiscount }}%</strong></span>
              <span v-if="viewItem.paymentModes[0].annualDiscount" class="text-caption">Anual: <strong>{{ viewItem.paymentModes[0].annualDiscount }}%</strong></span>
              <span v-if="!viewItem.paymentModes[0].monthlyDiscount && !viewItem.paymentModes[0].annualDiscount" class="text-caption grey--text">Sin descuentos</span>
            </v-sheet>
          </template>

          <!-- Igual que en el formulario: no aplica a TPA (no vende membresías) -->
          <template v-if="!viewIsUnitBilled">
            <div class="section-label d-flex align-center mb-3 mt-4">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Condiciones de la membresía</span>
              <v-divider />
            </div>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text text--darken-1">Duración</div>
                <div class="text-body-2 font-weight-medium">{{ viewItem.duracionMeses != null ? `${viewItem.duracionMeses} meses` : '—' }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text text--darken-1">Renovación de membresías</div>
                <div class="text-body-2 font-weight-medium">{{ membershipRenewalModeLabel(viewItem.membershipRenewalMode) }}</div>
              </v-col>
            </v-row>
          </template>
          </template>

          <template v-if="viewTab === 'planes'">
          <template v-if="viewItem.planes && viewItem.planes.length > 0">
            <div class="section-label d-flex align-center mb-3 mt-4">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Planes</span>
              <v-divider />
            </div>
            <!-- Activos primero; los inactivos quedan bajo el botón, igual que al editar. -->
            <template v-for="(cp, idx) in viewPlanRows">
            <v-btn
              v-if="cp.visible === false && idx === viewActivePlanCount"
              :key="`view-toggle-${idx}`"
              text
              small
              class="mb-1 px-1 grey--text text--darken-1"
              @click="showViewInactivePlans = !showViewInactivePlans"
            >
              <v-icon left small>{{ showViewInactivePlans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showViewInactivePlans
                ? 'Ocultar planes inactivos'
                : `Mostrar ${viewPlanRows.length - viewActivePlanCount} ${viewPlanRows.length - viewActivePlanCount === 1 ? 'plan inactivo' : 'planes inactivos'}` }}
            </v-btn>
            <div v-if="cp.visible !== false || showViewInactivePlans" :key="idx" class="mb-1">
              <v-sheet :color="cp.visible === false ? 'grey lighten-3' : 'blue lighten-5'" rounded class="pa-2 d-flex align-center">
                <v-icon small :color="cp.visible === false ? 'grey darken-1' : 'blue darken-2'" class="mr-2">mdi-tag-outline</v-icon>
                <span :class="['text-caption', cp.visible === false ? 'grey--text text--darken-1' : 'blue--text text--darken-2']">
                  <strong>{{ cp.plan && cp.plan.name ? cp.plan.name : ((planes.find(p => p.id === cp.planId) || {}).name || cp.planId) }}</strong>
                  &nbsp;·&nbsp;
                  {{ (cp.precioMensualNeto || 0).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 4 }) }} {{ cp.monedaPlan || '—' }}/mes
                  <template v-if="cp.type">&nbsp;·&nbsp;{{ planTypeLabel(cp.type) }}</template>
                  <v-chip v-if="cp.visible === false" x-small color="grey" dark class="ml-1">inactivo</v-chip>
                </span>
              </v-sheet>
            </div>
            </template>
          </template>

          <template v-if="viewItem.upsellPlans && viewItem.upsellPlans.length > 0">
            <div class="section-label d-flex align-center mb-3 mt-4">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Si un colaborador pasa su límite</span>
              <v-divider class="mr-3" />
              <v-chip x-small outlined color="grey darken-2"><v-icon x-small left>mdi-account-outline</v-icon>Paga el colaborador</v-chip>
            </div>
            <div v-for="(up, idx) in viewItem.upsellPlans" :key="`view-upsell-${idx}`" class="mb-1">
              <v-sheet color="orange lighten-5" rounded class="pa-2 d-flex align-center">
                <v-icon small color="orange darken-3" class="mr-2">mdi-arrow-up-bold-circle-outline</v-icon>
                <span class="text-caption orange--text text--darken-4">
                  <strong>{{ (up.plan && up.plan.name) || up.planId }}</strong>
                  <template v-if="up.type">&nbsp;·&nbsp;{{ planTypeLabel(up.type) }}</template>
                  &nbsp;·&nbsp;Precio neto:
                  <strong>{{ Number(up.precioMensualNeto || 0).toLocaleString('es-CL', { maximumFractionDigits: 4 }) }} {{ up.monedaPlan || '' }}</strong>
                </span>
              </v-sheet>
            </div>
          </template>


          </template>

          <template v-if="viewTab === 'facturacion'">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mt-2">Precio por unidad</div>
              <div class="text-body-2 font-weight-medium">
                {{ viewItem.billingTerms && viewItem.billingTerms.unitPrice != null
                  ? formatMoney(viewItem.billingTerms.unitPrice, viewItem.billingTerms.currency)
                  : 'Sin definir' }}
              </div>
            </v-col>
          </v-row>
          </template>

          <!-- Historial de estados: el backend registra cada transición con su
               fecha y motivo desde que existe la tabla, así que un contrato
               anterior a eso muestra el vacío en vez de inventar entradas. -->
          <div v-if="showViewHistory" class="mt-4">
            <div class="section-label d-flex align-center mb-3">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Historial de estados</span>
              <v-divider />
            </div>
                <div v-if="loadingHistory" class="text-center py-4">
                  <v-progress-circular indeterminate size="24" color="primary" />
                </div>
                <div
                  v-else-if="!statusHistory.length"
                  class="text-caption grey--text text--darken-1 py-2"
                >
                  Sin transiciones registradas. El historial empezó a guardarse
                  recientemente, así que los cambios anteriores no figuran.
                </div>
                <v-timeline v-else dense class="pt-0">
                  <v-timeline-item
                    v-for="h in statusHistoryDesc"
                    :key="h.id"
                    small
                    :color="historyColor(h.newStatus)"
                  >
                    <div class="d-flex align-center" style="gap: 6px;">
                      <ContractStatusChip :status="h.newStatus" />
                      <span v-if="h.previousStatus" class="text-caption grey--text">
                        desde {{ h.previousStatus }}
                      </span>
                    </div>
                    <div class="text-caption grey--text text--darken-1">
                      {{ formatHistoryDate(h.createdAt) }}
                      <span v-if="h.endReason"> · {{ endReasonLabel(h.endReason) }}</span>
                    </div>
                    <div v-if="h.reason" class="text-caption">{{ h.reason }}</div>
                  </v-timeline-item>
                </v-timeline>
          </div>

          <template v-if="viewTab === 'facturacion-adicional'">
            <v-row v-if="hasExtras(viewItem)" dense>
              <v-col v-if="viewItem.collectionFeeAmount != null" cols="6">
                <div class="text-caption grey--text text--darken-1">Por recaudación</div>
                <div class="text-body-2">{{ viewItem.collectionFeeAmount }} {{ viewItem.collectionFeeUnit }}</div>
              </v-col>
              <v-col v-if="viewItem.commissionAmount != null" cols="6">
                <div class="text-caption grey--text text--darken-1">Por comisión</div>
                <div class="text-body-2">{{ viewItem.commissionAmount }} {{ viewItem.commissionUnit }}</div>
              </v-col>
              <v-col v-if="viewItem.managementFeeAmount != null" cols="6">
                <div class="text-caption grey--text text--darken-1">Por gestión</div>
                <div class="text-body-2">{{ viewItem.managementFeeAmount }} {{ viewItem.managementFeeUnit }}</div>
              </v-col>
            </v-row>
            <div v-else class="text-body-2 grey--text text--darken-1">Sin facturación adicional.</div>
          </template>

          <template v-if="viewTab === 'comunicaciones'">
            <div class="section-label d-flex align-center mb-3">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Preferencias de correo</span>
              <v-divider />
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-chip small class="mr-1 mb-1" :color="viewItem.sendWelcomeEmail !== false ? 'green lighten-4' : 'grey lighten-3'">
                  Bienvenida: {{ viewItem.sendWelcomeEmail !== false ? 'Se envía' : 'Suprimido' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" :color="viewItem.sendContractAssignedEmail !== false ? 'green lighten-4' : 'grey lighten-3'">
                  Asignación de contrato: {{ viewItem.sendContractAssignedEmail !== false ? 'Se envía' : 'Suprimido' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" :color="viewItem.sendMembershipActivatedEmail !== false ? 'green lighten-4' : 'grey lighten-3'">
                  Activación de membresía: {{ viewItem.sendMembershipActivatedEmail !== false ? 'Se envía' : 'Suprimido' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" :color="viewItem.sendMembershipDeactivatedEmail !== false ? 'green lighten-4' : 'grey lighten-3'">
                  Desactivación de membresía: {{ viewItem.sendMembershipDeactivatedEmail !== false ? 'Se envía' : 'Suprimido' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" :color="viewItem.allowAdditionalPetFromApp !== false ? 'green lighten-4' : 'grey lighten-3'">
                  Mascotas adicionales en App: {{ viewItem.allowAdditionalPetFromApp !== false ? 'Permitido' : 'Bloqueado' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" :color="viewItem.ageBlocksContracting === true ? 'orange lighten-4' : 'grey lighten-3'">
                  Edad bloqueante: {{ viewItem.ageBlocksContracting === true ? 'Sí' : 'No' }}
                </v-chip>
              </v-col>
              <v-col cols="12">
                <div class="text-caption grey--text text--darken-1">Template de bienvenida</div>
                <div class="text-body-2">{{ viewItem.welcomeEmailTemplateId || 'Estándar' }}</div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="py-3 px-4">
          <v-btn text small color="primary" class="px-1" @click="showViewHistory = !showViewHistory">
            {{ showViewHistory ? 'Ocultar historial de estados' : 'Ver historial de estados' }}
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="viewDialog = false">Cerrar</v-btn>
          <v-btn color="primary" depressed @click="editFromView">
            <v-icon left small>mdi-pencil</v-icon>Editar {{ viewTabLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog crear/editar (sin persistent → se cierra con Esc).
         El formulario va repartido en pestañas y el panel tiene alto fijo: así el
         dialog no cambia de alto al cambiar de pestaña ni al cambiar la línea de
         negocio, y el botón Guardar no se mueve de lugar. -->
    <v-dialog v-model="dialog" max-width="1180" @keydown.esc="closeDialog">
      <v-card class="contrato-dialog d-flex">
        <div class="contrato-dialog__form d-flex flex-column">
        <v-card-title class="text-h6 pb-2">
          <span>{{ editingId ? 'Editar contrato' : 'Nuevo contrato' }}</span>
          <v-chip v-if="form.lineaNegocio" x-small :color="lineaColor(form.lineaNegocio)" dark class="ml-3">
            {{ form.lineaNegocio }}
          </v-chip>
          <v-spacer />
          <v-btn icon small @click="closeDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- `flex-grow-0`: v-tabs y v-divider traen flex-grow 1 de Vuetify y, dentro de
             esta columna flex, se repartían el alto con el formulario. -->
        <v-tabs v-model="activeTab" show-arrows class="flex-grow-0">
          <v-tab v-for="t in formTabs" :key="t.value" :href="`#${t.value}`">
            {{ t.text }}
            <!-- El contador solo aparece después de intentar guardar: al abrir un
                 contrato nuevo casi todo está vacío y marcarlo todo en rojo de
                 entrada no informa nada. -->
            <v-badge
              v-if="showValidation && tabErrors[t.value].length"
              inline
              color="error"
              :content="tabErrors[t.value].length"
            />
          </v-tab>
        </v-tabs>

        <v-divider class="flex-grow-0" />

        <v-alert
          v-if="error"
          type="error" dense outlined dismissible
          class="ma-4 mb-0"
          @input="error = null"
        >{{ error }}</v-alert>

        <v-card-text class="contrato-tabs-panel pt-4">
          <v-form ref="form">
            <!-- `eager`: todas las pestañas se montan de entrada. Sin eso, los
                 campos de una pestaña que nunca se abrió no quedan registrados en
                 el v-form y validate() los daría por válidos. -->
            <v-tabs-items v-model="activeTab">

            <v-tab-item value="general" eager>

            <!-- ═══ IDENTIFICACIÓN ════════════════════════════════════ -->
            <div class="section-label d-flex align-center mb-3">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Identificación</span>
              <v-divider />
            </div>

            <v-row dense>
              <v-col cols="12" md="6">
                <!-- Un piloto no se distingue de un contrato comercial en los
                     datos: misma duración, mismos planes. Si no se marca acá,
                     después no hay forma de saberlo. -->
                <v-select
                  v-model="form.tipoContrato"
                  :items="tipoContratoItems"
                  label="Tipo de contrato"
                  dense outlined
                  :hint="tipoContratoHint"
                  persistent-hint
                />
              </v-col>

              <!-- Estado (solo al editar) -->
              <v-col v-if="editingId" cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="contractStatuses"
                  label="Estado"
                  dense outlined
                  @change="onStatusChange"
                />
              </v-col>
              <v-col v-else cols="12" md="6" class="d-none d-md-block" />

              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.companyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Empresa *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <!-- La línea se fija al crear: el código del contrato y, en
                     Employee Benefit, sus filas de facturación se derivan de
                     ella una sola vez. El backend rechaza el cambio igual; esto
                     evita llegar al error. -->
                <v-select
                  v-model="form.lineaNegocio"
                  :items="lineas"
                  label="Línea de negocio *"
                  dense outlined
                  :disabled="!!editingId"
                  :hint="editingId ? 'No se puede cambiar: el código y la facturación se derivan de la línea. Crea el contrato de nuevo en la línea correcta.' : ''"
                  :persistent-hint="!!editingId"
                  :rules="[v => !!v || 'Requerido']"
                  @change="onLineaChange"
                />
              </v-col>

              <!-- Modalidad de contratación: solo en Employee Benefit, donde es obligatoria.
                   Colectivo y bolsa tienen precios de distinta naturaleza. -->
              <v-col v-if="showsHiringModality" cols="12" md="6">
                <v-select
                  v-model="form.hiringModality"
                  :items="hiringModalityItems"
                  label="Modalidad de contratación *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <!-- Producto. En Employee Benefit no se elige: lo fija la línea de
                   negocio (ver onLineaChange). Se muestra igual, bloqueado, porque
                   esconderlo dejaba al contrato sin decir con qué producto quedó. -->
              <v-col v-if="form.lineaNegocio" cols="12" md="6">
                <v-text-field
                  v-if="form.lineaNegocio === 'B2B Employee Benefit'"
                  :value="productoNombre"
                  label="Producto"
                  dense outlined readonly
                  background-color="grey lighten-4"
                  prepend-inner-icon="mdi-package-variant-closed"
                  hint="Lo fija la línea de negocio"
                  persistent-hint
                />
                <v-autocomplete
                  v-else
                  v-model="form.productId"
                  :items="productosFiltrados"
                  item-text="nombre"
                  item-value="id"
                  label="Producto *"
                  dense outlined clearable
                  :rules="[v => !!v || 'Requerido']"
                  @change="onProductoChange"
                />
              </v-col>

              <!-- País y moneda no se editan: se derivan de la empresa y se ven en el resumen. -->

              <!-- Default del país: sus planes se ofrecen a usuarios sin convenio o con el
                   convenio terminado. Uno por país; marcar otro pide confirmar el cambio. -->
              <v-col v-if="form.lineaNegocio === countryDefaultLine" cols="12">
                <v-checkbox
                  v-model="form.isCountryDefault"
                  :label="`Contrato default de ${paisDerived === '—' ? 'su país' : paisDerived}`"
                  hint="Sus planes se muestran en la app y en Felix a usuarios sin convenio o cuyo convenio terminó."
                  persistent-hint
                  dense
                  class="mt-0"
                />
              </v-col>
            </v-row>



            <!-- Vigencia y facturación: lo primero que se mira de un contrato -->
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.startDate"
                  label="Fecha de inicio *"
                  type="date"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.endDate"
                  :label="isChurned ? 'Fecha de término *' : 'Fecha de término'"
                  type="date"
                  dense outlined
                  :clearable="!isChurned"
                  :rules="endDateRules"
                  :hint="isChurned ? 'Fecha en que terminó el servicio, no en que se registra la baja' : ''"
                  :persistent-hint="isChurned"
                  @click:clear="form.endDate = null"
                />
              </v-col>
            </v-row>

            <!-- Canal, broker y ejecutivo -->
            <v-row dense>
              <v-col cols="12" :md="form.canalTipo === 'Broker' ? 4 : 6">
                <v-select
                  v-model="form.canalTipo"
                  :items="['Directo', 'Broker']"
                  label="Canal"
                  dense outlined
                  @change="onCanalTipoChange"
                />
              </v-col>

              <!-- Broker (solo si canalTipo = Broker) -->
              <v-col v-if="form.canalTipo === 'Broker'" cols="12" md="4">
                <v-autocomplete
                  v-model="form.brokerId"
                  :items="brokersActivos"
                  item-text="name"
                  item-value="id"
                  label="Broker *"
                  dense outlined
                  :rules="form.canalTipo === 'Broker' ? [v => !!v || 'Requerido'] : []"
                />
              </v-col>

              <v-col cols="12" :md="form.canalTipo === 'Broker' ? 4 : 6">
                <v-autocomplete
                  v-model="form.executiveId"
                  :items="ejecutivos"
                  :item-text="e => `${e.name} ${e.surname}`"
                  item-value="id"
                  label="Ejecutivo *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
            </v-row>

            <!-- Modalidad: solo en contratos B2C vendidos por un broker. El reporte B2C
                 clasifica las ventas del broker con este dato, por eso es obligatorio. -->
            <v-row v-if="showsBrokerModality" dense>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.brokerModality"
                  :items="brokerModalityItems"
                  label="Modalidad de broker *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
            </v-row>


            <!-- ═══ DOMINIOS (no aplica a TPA) ════════════════════════ -->
            <template v-if="!isUnitBilled">
            <div class="section-label d-flex align-center mb-3 mt-2">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Dominios</span>
              <v-divider />
            </div>

            <v-row dense>
              <v-col cols="12">
                <div
                  v-for="(item, idx) in form.domains"
                  :key="idx"
                  class="d-flex align-center mb-2"
                  style="gap: 8px"
                >
                  <v-text-field
                    v-model="item.domain"
                    :label="`Dominio ${idx + 1}`"
                    placeholder="ej: empresa.com"
                    dense outlined hide-details
                    style="flex: 1"
                    :rules="[v => !!v || 'Requerido']"
                  />
                  <v-btn icon small color="pink" class="mt-0" @click="askRemoveDomain(idx)" title="Quitar dominio">
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
                <v-btn text color="primary" small @click="addDomain" class="mt-1">
                  <v-icon left small>mdi-plus</v-icon>Agregar dominio
                </v-btn>
              </v-col>
            </v-row>
            </template>

            </v-tab-item>

            <v-tab-item value="comercial" eager>

            <!-- ═══ FACTURACIÓN ══════════════════════════════════════ -->
            <!-- El encabezado también da el aire de arriba: sin él, las etiquetas
                 de la primera fila quedaban cortadas contra las pestañas. -->
            <div class="section-label d-flex align-center mb-3 mt-2">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Facturación</span>
              <v-divider />
            </div>

            <!-- Tipo de facturación + Se factura por -->
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.tipoFacturacion"
                  :items="['Mensual', 'Anual']"
                  label="Tipo de facturación *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>

              <!-- Se factura por (base de cobro del contrato) -->
              <!-- TPA: la deriva el producto elegido, no es una elección libre -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.billingBasis"
                  :items="billingBasisItems"
                  label="Se factura por"
                  dense outlined
                  :disabled="isUnitBilled"
                  :hint="isUnitBilled ? 'Definido automáticamente por el producto' : ''"
                  persistent-hint
                />
              </v-col>
            </v-row>


            <!-- Monto (Employee Benefit y los contratos de monto fijo; no aplica a TPA) -->
            <v-row v-if="showNetAmount && !isUnitBilled" dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="netAmountDisplay"
                  :label="`Monto contrato (${netAmountCurrencyLabel}) *`"
                  inputmode="numeric"
                  dense outlined
                  :rules="[v => (v !== null && v !== undefined && v !== '') || 'Requerido']"
                  :hint="mrrHint"
                  persistent-hint
                />
              </v-col>
              <!-- La moneda no se puede derivar del país: hay contratos
                   peruanos facturados en CLP y en USD. -->
              <v-col v-if="isFixedAmount" cols="12" md="6">
                <v-select
                  v-model="form.netAmountCurrency"
                  :items="currencyItems"
                  :label="`Moneda del monto`"
                  dense outlined
                  :hint="`Por defecto ${monedaDerived}`"
                  persistent-hint
                />
              </v-col>
            </v-row>


            <!-- ═══ DESCUENTOS (no aplica a TPA) ══════════════════════ -->
            <template v-if="!isUnitBilled">
            <div class="section-label d-flex align-center mb-3 mt-2">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Descuentos</span>
              <v-divider />
            </div>

            <!-- Siempre presentes: un contrato sin descuento es 0 %, no "sin dato".
                 Dejarlos opcionales hacía que no se distinguiera un contrato sin
                 descuento de uno al que nadie se lo cargó. -->
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="descuentoMensualPct"
                  label="Desc. mensual (%) *"
                  type="number" min="0" max="100"
                  suffix="%"
                  dense outlined
                  :rules="descuentoRules"
                  hint="0 si el contrato no tiene descuento"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="descuentoAnualPct"
                  label="Desc. anual (%) *"
                  type="number" min="0" max="100"
                  suffix="%"
                  dense outlined
                  :rules="descuentoRules"
                  hint="0 si el contrato no tiene descuento"
                  persistent-hint
                />
              </v-col>
            </v-row>
            </template>

            <!-- ═══ CONDICIONES COMERCIALES (no aplica a TPA: sin membresías del contrato) ══ -->
            <template v-if="!isUnitBilled">
            <div class="section-label d-flex align-center mb-3 mt-2">
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Condiciones comerciales</span>
              <v-divider />
            </div>

            <v-row dense>
              <!-- Membresías contratadas: EB y B2B Asistencias. Algunos clientes -->
              <!-- pactan una cantidad definida; otros no, de ahí el mismo patrón -->
              <!-- "clearable" que Límite por colaborador: vacío = sin límite definido. -->
              <template v-if="['B2B Employee Benefit', 'B2B Asistencias'].includes(form.lineaNegocio)">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.membershipQuota"
                    label="Membresías contratadas"
                    type="number" min="1"
                    dense outlined clearable
                    hint="Bolsa que paga la empresa. Vacío = sin bolsa definida"
                    persistent-hint
                    @click:clear="form.membershipQuota = null"
                  />
                </v-col>
              </template>

              <!-- Límite por colaborador: no aplica a TPA (no factura por membresías del contrato) -->
              <v-col v-if="!isUnitBilled" cols="12" md="6">
                <v-text-field
                  v-model.number="form.membershipLimit"
                  label="Límite por colaborador"
                  type="number" min="0"
                  dense outlined clearable
                  hint="Membresías que cubre la empresa a cada colaborador. Vacío = sin límite"
                  persistent-hint
                  @click:clear="form.membershipLimit = null"
                />
              </v-col>
            </v-row>
            </template>

            <!-- ═══ CONDICIONES DE LA MEMBRESÍA (no aplica a TPA: no vende membresías) ══ -->
            <template v-if="!isUnitBilled">
              <div class="section-label d-flex align-center mb-3 mt-2">
                <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Condiciones de la membresía</span>
                <v-divider />
              </div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.duracionMeses"
                    label="Duración (meses)"
                    type="number" min="1"
                    dense outlined
                    :rules="[v => (v !== null && v !== '' && v >= 1) || 'Mínimo 1 mes']"
                    hint="Vigencia de las membresías"
                    persistent-hint
                  />
                </v-col>

                <!-- Qué hace el cron diario con las membresías vencidas del contrato -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.membershipRenewalMode"
                    :items="membershipRenewalModeOptions"
                    label="Renovación de membresías"
                    dense outlined
                    hint="Qué pasa con las membresías al vencer"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </template>

            </v-tab-item>

            <v-tab-item value="planes" eager>

            <!-- ═══ PLANES (no aplica a TPA) ═══════════════════════════
                 Dos bloques según quién paga: lo que cubre la empresa y lo que paga
                 el colaborador cuando pasa su límite. -->
            <template v-if="!isUnitBilled">
            <div class="section-label d-flex align-center mb-1 mt-2">
              <v-icon small color="primary" class="mr-2">{{ isEbContract ? 'mdi-domain' : 'mdi-tag-outline' }}</v-icon>
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">{{ isEbContract ? 'Paga la empresa' : 'Planes del contrato' }}</span>
              <v-divider />
            </div>
            <div v-if="coverageText" class="text-body-2 grey--text text--darken-1 mb-4">
              {{ coverageText }} · se editan en Comercial
            </div>
            <div v-else class="mb-3" />
            <!-- Los planes que ya venían inactivos van al final, colapsados tras el
                 botón, para que no tapen a los activos; uno que se apaga al editar
                 sigue en su lugar. v-if y no v-show: d-flex lleva display !important. -->
            <template v-for="{ item, idx, collapsed, firstCollapsed } in planRows">
            <div v-if="firstCollapsed" :key="`toggle-${idx}`">
              <v-btn
                text
                small
                class="mb-2 grey--text text--darken-1"
                @click="showInactivePlans = !showInactivePlans"
              >
                <v-icon left small>{{ showInactivePlans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ showInactivePlans
                  ? 'Ocultar planes inactivos'
                  : `Mostrar ${collapsedInactiveCount} ${collapsedInactiveCount === 1 ? 'plan inactivo' : 'planes inactivos'}` }}
              </v-btn>
            </div>
            <div
              v-if="!collapsed || showInactivePlans"
              :key="idx"
              class="d-flex align-start mb-2 py-1 rounded"
              :style="{
                gap: '8px',
                backgroundColor: item.visible === false ? '#f5f5f5' : 'transparent',
                opacity: item.visible === false ? 0.6 : 1,
                transition: 'background-color 0.2s, opacity 0.2s',
              }"
            >
              <v-switch
                v-model="item.visible"
                dense hide-details
                class="mt-1 flex-shrink-0"
                title="Visible"
                :disabled="item.planVisible === false"
                style="flex: 0 0 50px"
              />
              <v-autocomplete
                v-model="item.planId"
                :items="planesConActual(idx)"
                item-text="name"
                item-value="id"
                :label="`Plan ${idx + 1}`"
                dense outlined hide-details
                :disabled="item.visible === false"
                style="flex: 1 1 auto; min-width: 130px"
                @change="onPlanChange(idx)"
              />
              <v-select
                v-model="item.type"
                :items="planTypeItems"
                label="Tipo de cobro"
                dense outlined hide-details
                :disabled="item.visible === false"
                style="flex: 0 0 120px; min-width: 120px; max-width: 120px"
              />
              <v-select
                v-if="monedaDerived === 'CLP' && !isEbContract"
                v-model="item.monedaPlan"
                :items="['CLP', 'UF']"
                dense outlined hide-details
                :disabled="item.visible === false"
                style="flex: 0 0 92px; min-width: 92px; max-width: 92px"
              />
              <!-- En EB el precio solo cobra las membresías sobre la bolsa: va en su propia sección. -->
              <v-text-field
                v-if="!isEbContract"
                v-model.number="item.precioMensualNeto"
                :label="`Precio neto (${item.monedaPlan || monedaDerived})`"
                type="number"
                dense outlined hide-details
                style="flex: 0 0 150px; min-width: 150px; max-width: 150px"
              />
              <v-btn
                icon
                small
                color="pink"
                class="mt-1"
                @click="askRemovePlan(idx)"
                title="Quitar plan"
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            </template>

            <v-btn
              text
              color="primary"
              small
              @click="addPlan"
              class="mt-1"
            >
              <v-icon left small>mdi-plus</v-icon>
              Agregar plan
            </v-btn>

            <!-- En EB el precio del plan solo cobra las membresías sobre la bolsa;
                 es el mismo `precioMensualNeto` que usa el MRR del backend. -->
            <template v-if="isEbContract">
              <v-divider class="my-4" />
              <div class="text-body-1 font-weight-medium">{{ overQuotaTitle }}</div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">Cada membresía activa sobre la bolsa se suma a la factura de la empresa.</div>
            <div
              v-for="(item, idx) in form.planes"
              :key="`over-quota-${idx}`"
              class="d-flex align-center mb-2"
              style="gap: 8px"
            >
              <span class="text-body-1" style="flex: 1 1 auto">{{ planName(item.planId) || item.planName || `Plan ${idx + 1}` }}</span>
              <v-select
                v-if="monedaDerived === 'CLP'"
                v-model="item.monedaPlan"
                :items="['CLP', 'UF']"
                label="Moneda"
                dense outlined hide-details
                style="flex: 0 0 100px; max-width: 100px"
              />
              <v-text-field
                v-model.number="item.precioMensualNeto"
                :label="`Precio por membresía extra (${item.monedaPlan || monedaDerived})`"
                type="number"
                min="0"
                dense outlined hide-details
                suffix="al mes"
                style="flex: 0 0 260px; max-width: 260px"
              />
            </div>
            </template>

            <!-- Planes de upsell: los paga el colaborador cuando el convenio ya no lo cubre.
                 La membresía queda en este contrato pero no se le cobra a la empresa. -->
            <div class="section-label d-flex align-center mb-1 mt-6">
              <v-icon small color="primary" class="mr-2">mdi-account-outline</v-icon>
              <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Paga el colaborador</span>
              <v-divider />
            </div>
            <div class="text-body-2 grey--text text--darken-1 mb-3">{{ overLimitCaption }}</div>
            <!-- Solo de muestra: el backend aún no guarda estas opciones. Reflejan lo que pasa hoy. -->
            <div class="d-flex align-center mb-1 mt-2" style="gap: 8px">
              <v-switch :input-value="false" disabled dense hide-details class="ma-0 pa-0" style="flex: 0 0 50px" />
              <span class="text-body-1">{{ contractPlanNames }}</span>
              <v-chip small outlined color="grey darken-2">Próximamente</v-chip>
            </div>
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-switch :input-value="offersCountryB2c" disabled dense hide-details class="ma-0 pa-0" style="flex: 0 0 50px" />
              <span class="text-body-1">Planes B2C actuales{{ paisDerived === '—' ? '' : ` de ${paisDerived}` }}</span>
              <span v-if="!offersCountryB2c" class="text-caption grey--text text--darken-1">· no se ofrecen porque el contrato tiene planes propios</span>
            </div>
            <div
              v-for="(row, idx) in form.upsellPlans"
              :key="`upsell-${idx}`"
              class="d-flex align-start mb-2"
              style="gap: 8px"
            >
              <v-autocomplete
                v-model="row.planId"
                :items="upsellPlanOptions(idx)"
                item-text="name"
                item-value="id"
                :label="`Plan ${idx + 1}`"
                dense outlined hide-details
                style="flex: 1 1 0; min-width: 140px"
              />
              <!-- Hoy hay un solo disparador (mascota adicional sin cupo): el selector no aporta. -->
              <v-select
                v-if="upsellTriggerItems.length > 1"
                v-model="row.trigger"
                :items="upsellTriggerItems"
                label="Se ofrece cuando"
                dense outlined hide-details
                style="flex: 0 1 170px; min-width: 130px"
              />
              <v-select
                v-model="row.type"
                :items="planTypeItems"
                label="Tipo de cobro"
                dense outlined hide-details
                style="flex: 0 0 120px; min-width: 120px; max-width: 120px"
              />
              <v-select
                v-if="monedaDerived === 'CLP'"
                v-model="row.monedaPlan"
                :items="['CLP', 'UF']"
                dense outlined hide-details
                style="flex: 0 0 92px; min-width: 92px; max-width: 92px"
              />
              <v-text-field
                v-model.number="row.precioMensualNeto"
                :label="`Precio neto (${row.monedaPlan || monedaDerived})`"
                type="number"
                min="0"
                dense outlined hide-details
                style="flex: 0 0 130px; min-width: 130px; max-width: 130px"
              />
              <v-btn icon small color="pink" class="mt-1 flex-shrink-0" title="Quitar plan" @click="removeUpsellPlan(idx)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-btn text color="primary" small class="mt-1" @click="addUpsellPlan">
              <v-icon left small>mdi-plus</v-icon>
              Agregar otro plan
            </v-btn>
            <v-switch
              v-model="form.allowAdditionalPetFromApp"
              label="Permitir agregar mascotas adicionales desde la App"
              hint="Si se desactiva, al agotar sus cupos el usuario no podrá contratar planes para otra mascota desde la App"
              persistent-hint
              inset dense
              class="mt-4"
            />
            <v-switch
              v-model="form.ageBlocksContracting"
              label="La edad es bloqueante en la contratación"
              hint="Si se activa, los planes con algún servicio fuera del rango de edad de la mascota no se muestran ni se pueden contratar para ella"
              persistent-hint
              inset dense
              class="mt-4"
            />
            </template>

            </v-tab-item>

            <v-tab-item value="facturacion" eager>



            <!-- ═══ FACTURACIÓN POR UNIDAD (solo TPA) ════════════════ -->
            <template v-if="isUnitBilled">
              <div class="section-label d-flex align-center mb-3 mt-2">
                <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Facturación por unidad</span>
                <v-divider />
              </div>

              <!-- Qué servicios/planes factura este contrato se asigna desde el -->
              <!-- maintainer de Servicios (services-plans), no aquí. -->
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="form.billingUnitPrice"
                    label="Precio por unidad *"
                    type="number" min="0"
                    dense outlined
                    hint="Precio por unidad facturada"
                    persistent-hint
                    :rules="[
                      v => (v !== null && v !== undefined && v !== '') || 'Requerido',
                      v => v >= 0 || 'No puede ser negativo',
                    ]"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    v-model="form.billingCurrency"
                    :items="['CLP', 'USD', 'UF', 'PEN']"
                    label="Moneda"
                    dense outlined
                  />
                </v-col>

              </v-row>
            </template>

            </v-tab-item>

            <v-tab-item value="facturacion-adicional" eager>

            <!-- ═══ EXTRAS (no aplican a las líneas por unidad) ═══ -->
            <template v-if="!isUnitBilled">
              <div class="section-label d-flex align-center mb-3 mt-2">
                <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Extras</span>
                <v-divider />
              </div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.collectionFeeAmount"
                    label="Por recaudación"
                    type="number" min="0" step="any"
                    dense outlined clearable
                    hint="Recurrente: se cobra por cada membresía activa del mes"
                    persistent-hint
                    @click:clear="form.collectionFeeAmount = null"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.collectionFeeUnit"
                    :items="extraUnitItems"
                    label="Unidad"
                    dense outlined clearable
                    @click:clear="form.collectionFeeUnit = null"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.commissionAmount"
                    label="Por comisión"
                    type="number" min="0" step="any"
                    dense outlined clearable
                    hint="Recurrente: se cobra por cada membresía activa del mes"
                    persistent-hint
                    @click:clear="form.commissionAmount = null"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.commissionUnit"
                    :items="extraUnitItems"
                    label="Unidad"
                    dense outlined clearable
                    @click:clear="form.commissionUnit = null"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.managementFeeAmount"
                    label="Por gestión"
                    type="number" min="0" step="any"
                    dense outlined clearable
                    hint="Recurrente: se cobra por cada membresía activa del mes"
                    persistent-hint
                    @click:clear="form.managementFeeAmount = null"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.managementFeeUnit"
                    :items="extraUnitItems"
                    label="Unidad"
                    dense outlined clearable
                    @click:clear="form.managementFeeUnit = null"
                  />
                </v-col>
              </v-row>
            </template>

            </v-tab-item>

            <v-tab-item value="comunicaciones" eager>

            <!-- ═══ PREFERENCIAS DE CORREO (solo productos clasificados "user") ═══ -->
            <template v-if="isUserClassifiedProduct">
              <div class="section-label d-flex align-center mb-3 mt-2">
                <span class="text-caption font-weight-bold text-uppercase primary--text mr-3" style="white-space:nowrap">Preferencias de correo</span>
                <v-divider />
              </div>
              <!-- Suprimir un correo es una decisión que se revisa meses después:
                   cada fila dice cuándo se dispara y en qué estado quedó, con las
                   mismas palabras que la ficha de solo lectura. -->
              <v-sheet outlined rounded>
                <div
                  v-for="pref in emailPreferences"
                  :key="pref.field"
                  class="email-pref d-flex align-center px-4 py-2"
                  :class="{ 'email-pref--off': !form[pref.field] }"
                >
                  <v-icon :color="form[pref.field] ? 'primary' : 'grey'" class="mr-4">
                    {{ pref.icon }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium">{{ pref.label }}</div>
                    <div class="text-caption grey--text text--darken-1">{{ pref.hint }}</div>
                  </div>
                  <span
                    class="text-caption mr-3"
                    :class="form[pref.field] ? 'primary--text' : 'grey--text text--darken-1'"
                  >
                    {{ form[pref.field] ? 'Se envía' : 'Suprimido' }}
                  </span>
                  <v-switch
                    v-model="form[pref.field]"
                    :aria-label="pref.label"
                    inset dense hide-details
                    class="ma-0 pa-0"
                  />
                </div>
              </v-sheet>
              <!-- Independiente del switch de Bienvenida: suprimir el envío no
                   borra el template, así que al reactivarlo vuelve el mismo correo. -->
              <v-text-field
                v-model="form.welcomeEmailTemplateId"
                label="Template de bienvenida (SendGrid)"
                placeholder="d-…"
                hint="Opcional. Si está vacío se usa el template de bienvenida estándar."
                persistent-hint
                :rules="[v => !v || /^d-[0-9a-f]{32}$/.test(v.trim()) || 'Debe ser un ID de template de SendGrid (d- y 32 caracteres)']"
                dense outlined clearable
                class="mt-4"
              />
            </template>

            </v-tab-item>

            </v-tabs-items>
          </v-form>
        </v-card-text>
        </div>

        <!-- Resumen en vivo de lo que queda guardado y de lo que se va a facturar.
             Lista lo que falta desde que se abre: los requeridos de una pestaña
             cerrada no se ven, y sin esto Guardar parecía no hacer nada. -->
        <aside class="contrato-summary d-flex flex-column" aria-label="Resumen del contrato">
          <div class="contrato-summary__body d-flex flex-column">
          <div class="contrato-summary__eyebrow d-flex align-center">
            <span class="mr-3">Resumen</span>
            <div class="contrato-summary__rule flex-grow-1" />
          </div>

          <div>
            <div v-if="editingCodigo" class="contrato-summary__label mb-1">{{ editingCodigo }}</div>
            <div class="contrato-summary__company">{{ summaryCompanyName }}</div>
            <div class="contrato-summary__muted">{{ summarySubline }}</div>
            <div class="d-flex align-center mt-2">
              <span v-if="form.lineaNegocio" class="contrato-summary__chip">{{ form.lineaNegocio }}</span>
            </div>
          </div>

          <div class="contrato-summary__mrr">
            <div class="contrato-summary__label">MRR estimado</div>
            <div class="d-flex align-baseline flex-wrap">
              <span class="contrato-summary__mrr-value" :class="{ 'contrato-summary__mrr-value--empty': !summaryMrr.hasValue }">
                {{ summaryMrr.value }}
              </span>
              <span v-if="summaryMrr.suffix" class="contrato-summary__muted ml-2">{{ summaryMrr.suffix }}</span>
            </div>
            <div class="contrato-summary__caption">{{ summaryMrr.caption }}</div>
          </div>

          <div class="contrato-summary__facts">
            <div v-for="fact in summaryFacts" :key="fact.label" class="d-flex justify-space-between">
              <span class="contrato-summary__muted mr-3">{{ fact.label }}</span>
              <span class="contrato-summary__value" :class="{ 'contrato-summary__value--warn': fact.warn }">{{ fact.value }}</span>
            </div>
          </div>

          <div class="contrato-summary__rule" />

          <div v-if="summaryMissing.length">
            <div class="contrato-summary__label contrato-summary__label--warn mb-3">Falta completar</div>
            <div v-for="item in summaryMissing" :key="item" class="contrato-summary__missing d-flex align-center mb-2">
              <v-icon small color="#f5a3b8" class="mr-2">mdi-alert-outline</v-icon>{{ item }}
            </div>
          </div>
          <div v-else class="contrato-summary__ready d-flex align-center">
            <v-icon small color="#9fe0bb" class="mr-2">mdi-check</v-icon>Todo listo para guardar
          </div>
          </div>

          <div class="contrato-summary__footer d-flex justify-end">
            <v-btn text class="contrato-summary__cancel" @click="closeDialog">Cancelar</v-btn>
            <v-btn color="white" depressed class="primary--text ml-2" :loading="saving" @click="submit">
              {{ editingId ? 'Guardar cambios' : 'Crear contrato' }}
            </v-btn>
          </div>
        </aside>
      </v-card>
    </v-dialog>

    <!-- Confirmación al marcar un default en un país que ya tiene uno. -->
    <v-dialog :value="!!countryDefaultConflict" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">Ya hay un default definido</v-card-title>
        <v-card-text>
          El contrato <strong>{{ countryDefaultConflict }}</strong> ya es el default de
          {{ paisDerived === '—' ? 'este país' : paisDerived }}. ¿Deseas cambiarlo por este
          contrato o cancelar?
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="countryDefaultConflict = null">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="confirmReplaceCountryDefault">
            Cambiar default
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Avisos de las acciones del PDF del contrato -->
    <v-snackbar v-model="documentSnackbar.show" :timeout="5000" :color="documentSnackbar.color">
      {{ documentSnackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="documentSnackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ContratoService   from '../services/contrato.service';
import ProductoService   from '../services/producto.service';
import LineaNegocioService from '../services/linea-negocio.service';
import CompanyService    from '../services/companie.service';
import PlansService      from '../services/plans.service';
import BrokerService     from '../services/broker.service';
import EjecutivosService from '../services/ejecutivos.service';
import ContractStatusChip, { CONTRACT_STATUSES } from '../components/ContractStatusChip.vue';
import ContractNpsChip   from '../components/ContractNpsChip.vue';
import ContractNpsModal  from '../components/ContractNpsModal.vue';
import ContractDocumentActions from '../components/ContractDocumentActions.vue';
import { compareNpsSummaries } from '../utils/nps.utils';
import { sortItems }     from 'vuetify/lib/util/helpers';
import CurrencyUtils     from '../utils/currency.utils';
import CountryService    from '../services/countries.service';

// Tipo de cobro / método de pago de cada plan del contrato.
// Valores válidos del enum_plans_type en el backend (contrato_planes.type).
const PLAN_TYPE_ITEMS = [
  { text: 'EB', value: 'eb' },
  { text: 'Asist.', value: 'assistance' },
  { text: 'B2C', value: 'b2c' },
  { text: 'B2B2C', value: 'b2b2c' },
  { text: 'DxP', value: 'b2b2c-dxp' },
];

// Tipo de activación del contrato (base de cobro)
const BILLING_BASIS_ITEMS = [
  { text: 'Membresías activas', value: 'active_memberships' },
  { text: 'Usuarios del contrato', value: 'users_in_contract' },
  // Tarifa plana: no se cuenta nada. Es el caso de un servicio que se atiende
  // desde la plataforma del cliente, donde en Pawer no se cargan usuarios.
  { text: 'Monto fijo', value: 'fixed_amount' },
  { text: 'Por reembolso gestionado', value: 'reviewed_refunds' },
];

// Base de cobro que el backend exige para cada producto unit-billed, indexada por
// `producto.code` (clave estable, NO por `producto.nombre` — el nombre se puede renombrar
// libremente, ej. "TPA" -> "TPA de claims", sin que esto se rompa). Debe calzar exacto con
// BILLING_BASIS_BY_PRODUCT en unit-billing.constants.ts (backend).
const UNIT_BILLING_BASIS_BY_PRODUCT_CODE = {
  TPA: 'reviewed_refunds',
};

// Producto (`producto.code`) de cada línea facturada por unidad. Debe calzar con
// BUSINESS_LINE_BY_PRODUCT en unit-billing.constants.ts (backend).
const UNIT_BILLED_PRODUCT_CODES_BY_LINE = {
  TPA: ['TPA'],
};

const UNIT_BILLED_LINES = Object.keys(UNIT_BILLED_PRODUCT_CODES_BY_LINE);

/** Toda línea cuyos contratos facturan por unidad y no llevan planes ni descuentos. */
const isUnitBilledLine = linea => UNIT_BILLED_LINES.includes(linea);

/**
 * Qué es el contrato, más allá de su estado.
 *
 * Un piloto que termina no es una baja, y un contrato interno no es un
 * cliente: las métricas de adquisición y retención los tratan distinto, pero
 * ninguno se distingue de un contrato comercial mirando sus datos.
 */
const TIPO_CONTRATO_ITEMS = [
  { text: 'Comercial', value: 'comercial' },
  { text: 'Piloto', value: 'piloto' },
  { text: 'Demo / interno', value: 'demo' },
];

/**
 * Qué hace el cron diario con las membresías vencidas del contrato. Los
 * contratos churned o pausados se omiten siempre, sea cual sea el modo.
 */
const MEMBERSHIP_RENEWAL_MODE_ITEMS = [
  { text: 'No se renueva', value: 'none' },
  { text: 'Solo baja', value: 'deactivate' },
  { text: 'Renovación', value: 'renew' },
];

/** Motivos de término, tal como los guarda el backend. */
const END_REASON_LABELS = {
  churn: 'baja',
  renovacion: 'renovado',
  fin_programado: 'fin programado',
};

const TIPO_CONTRATO_HINTS = {
  comercial: 'Cuenta en adquisición y en churn',
  piloto: 'Cuenta en adquisición; al terminar no cuenta como baja',
  demo: 'Queda fuera de todas las métricas',
};

/** Monedas en que puede estar el monto de un contrato. */
// EUR queda por las filas históricas de Rimac, que se pueden seguir editando.
const CURRENCY_ITEMS = ['CLP', 'PEN', 'USD', 'MXN', 'EUR', 'UF'];

// Mapea el tipo de cobro del plan (enum_plans_type) al método de pago válido de
// contrato-payment-mode (enum: dxp | payment | eb | assistance). Se usa para
// asociar los descuentos del contrato al flujo de onboarding correspondiente.
const PLAN_TYPE_TO_PAYMENT_MODE = {
  eb: 'eb',
  b2b: 'eb',
  assistance: 'assistance',
  dxp: 'dxp',
  'b2b2c-dxp': 'dxp',
  b2c: 'payment',
  b2b2c: 'payment',
};

const emptyForm = () => ({
  companyId:        null,
  planes:           [],   // [{ planId, precioMensualNeto, monedaPlan, visible, type }]
  productId:        null,
  executiveId:      null,
  lineaNegocio:     'B2B Employee Benefit',
  canalTipo:        'Directo',
  brokerId:         null,
  brokerModality:   null,
  hiringModality:   null,
  canal:            'Directo',
  tipoFacturacion:  'Mensual',
  netAmount:        null,
  netAmountCurrency: null,
  membershipQuota:  null,
  membershipLimit:  1,
  billingBasis:     'users_in_contract',
  tipoContrato:     'comercial',
  // Fuera del cron hasta que se elija un modo.
  membershipRenewalMode: 'none',
  duracionMeses:    12,
  startDate:        '',
  endDate:          '',
  status:           'Activo',
  domains:     [],   // [{ domain }]
  descuentos:  { monthlyDiscount: 0, annualDiscount: 0 },
  // Facturación por unidad (solo TPA): ver contrato-billing-terms en el backend.
  billingUnitPrice:       null,
  billingCurrency:        'CLP',
  // Extras (se suman al MRR): columnas en contratos. Arrancan en 0 y no vacíos, para que un
  // contrato sin extras quede con un valor explícito en vez de un nulo. El backend exige que
  // monto y unidad vayan juntos, así que la unidad también trae default (la ajusta el watcher
  // de `monedaDerived` en cuanto se sabe el país de la empresa).
  collectionFeeAmount:    0,
  collectionFeeUnit:      'CLP',
  commissionAmount:       0,
  commissionUnit:         'CLP',
  managementFeeAmount:    0,
  managementFeeUnit:      'CLP',
  // Preferencias de correo (solo productos clasificados "user"): columnas en contratos.
  sendWelcomeEmail:              true,
  sendContractAssignedEmail:     true,
  sendMembershipDeactivatedEmail: true,
  sendMembershipActivatedEmail:   true,
  // Template propio para los correos de bienvenida; null = el estándar de cada flujo.
  welcomeEmailTemplateId:        null,
  // Contrato B2C cuyos planes se ofrecen en su país a usuarios sin convenio.
  isCountryDefault:              false,
  // Si el usuario puede contratar planes para otra mascota desde la App al agotar sus cupos.
  allowAdditionalPetFromApp:     true,
  // Si un plan con algún servicio fuera del rango de edad de la mascota deja de ofrecerse y contratarse.
  ageBlocksContracting:          false,
  // Planes pagados por el usuario cuando el convenio ya no lo cubre: [{ planId, trigger }].
  upsellPlans:                   [],
});

/** Cuándo se ofrece un plan de upsell. Debe calzar con `UPSELL_TRIGGERS` del backend. */
const UPSELL_TRIGGER_ITEMS = [
  { text: 'Mascota adicional sin cupo', value: 'additional_pet' },
];

/** Única línea de negocio cuyos contratos pueden ser el default de su país. */
const COUNTRY_DEFAULT_LINE = 'B2C';

/** Única línea cuyos contratos de broker llevan modalidad (debe coincidir con el backend). */
const BROKER_MODALITY_LINE = 'B2C';

const BROKER_MODALITY_ITEMS = [
  { text: 'Broker Integrado', value: 'integrated' },
  { text: 'Broker by Felix',  value: 'felix' },
];

/** Única línea cuyos contratos llevan modalidad de contratación (debe coincidir con el backend). */
const HIRING_MODALITY_LINE = 'B2B Employee Benefit';

const HIRING_MODALITY_ITEMS = [
  { text: 'Colectivo',        value: 'collective' },
  { text: 'Flexible (bolsa)', value: 'pool' },
];

const EXTRA_UNIT_ITEMS = ['CLP', 'USD', 'UF', 'PEN', 'ARS', 'MXN', '%'];

/** Fracción almacenada (0,2) -> porcentaje mostrado (20). */
const fraccionAPorcentaje = valor => {
  if (valor === null || valor === undefined || valor === '') return valor;
  return Math.round(Number(valor) * 10000) / 100;
};

/** Porcentaje escrito (20) -> fracción almacenada (0,2). */
const porcentajeAFraccion = valor => {
  if (valor === null || valor === undefined || valor === '') return valor;
  return Number(valor) / 100;
};

/**
 * Correos que el contrato puede suprimir, con el momento en que se disparan:
 * el nombre del campo solo no alcanza para decidir si conviene apagarlo.
 */
const EMAIL_PREFERENCES = [
  {
    field: 'sendWelcomeEmail',
    icon: 'mdi-email-outline',
    label: 'Bienvenida',
    hint: 'Al dar de alta al usuario en el contrato',
  },
  {
    field: 'sendContractAssignedEmail',
    icon: 'mdi-file-document-outline',
    label: 'Asignación de contrato',
    hint: 'Cuando el usuario queda cubierto por este contrato',
  },
  {
    field: 'sendMembershipActivatedEmail',
    icon: 'mdi-account-check-outline',
    label: 'Activación de membresía',
    hint: 'Cuando su membresía queda activa, también al renovar',
  },
  {
    field: 'sendMembershipDeactivatedEmail',
    icon: 'mdi-account-cancel-outline',
    label: 'Desactivación de membresía',
    hint: 'Cuando su membresía deja de estar activa',
  },
];

/**
 * Pestañas del formulario, en el orden en que se muestran. El `value` es también
 * el valor del v-model de las pestañas (v-tab usa `href="#value"`), así que la
 * pestaña activa no depende del índice: esconder una no descoloca al resto.
 */
const CONTRATO_TABS = [
  { value: 'general',     text: 'General' },
  { value: 'comercial',   text: 'Comercial' },
  { value: 'planes',      text: 'Planes' },
  { value: 'facturacion', text: 'Facturación' },
  // No aplica a las líneas facturadas por unidad (TPA): no llevan extras.
  { value: 'facturacion-adicional', text: 'Facturación adicional' },
  // Solo para productos clasificados "user": son los únicos que mandan estos correos.
  { value: 'comunicaciones', text: 'Comunicaciones' },
];

export default {
  name: 'Contratos',
  components: { ContractStatusChip, ContractNpsChip, ContractNpsModal, ContractDocumentActions },

  data() {
    return {
      planTypeItems:    PLAN_TYPE_ITEMS,
      billingBasisItems: BILLING_BASIS_ITEMS,
      extraUnitItems:   EXTRA_UNIT_ITEMS,
      emailPreferences: EMAIL_PREFERENCES,
      contractStatuses: CONTRACT_STATUSES,
      statusHistory: [],
      loadingHistory: false,
      tipoContratoItems: TIPO_CONTRATO_ITEMS,
      membershipRenewalModeOptions: MEMBERSHIP_RENEWAL_MODE_ITEMS,
      currencyItems: CURRENCY_ITEMS,
      loading:      false,
      saving:       false,
      deleting:     false,
      error:        null,
      dialog:       false,
      deleteDialog: false,
      deleteTarget: null,
      removePlanDialog: false,
      removePlanIdx: null,
      // Planes que ya venían inactivos al abrir el formulario (se muestran colapsados).
      collapsedPlans: [],
      showInactivePlans: false,
      removeDomainDialog: false,
      removeDomainIdx: null,
      viewDialog:   false,
      npsDialog:    false,
      documentSnackbar: { show: false, message: '', color: 'success' },
      npsItem:      null,
      tabs:         CONTRATO_TABS,
      viewItem:     null,
      editingId:    null,
      activeTab:    'general',
      viewTab:      'general',
      showViewHistory: false,
      showViewInactivePlans: false,
      countryDefaultLine: COUNTRY_DEFAULT_LINE,
      upsellTriggerItems: UPSELL_TRIGGER_ITEMS,
      // Código del default actual del país cuando el backend pide confirmar el cambio.
      countryDefaultConflict: null,
      // Los contadores de error por pestaña solo se muestran después de un intento
      // de guardar fallido: un contrato nuevo arranca casi vacío y marcarlo entero
      // en rojo al abrirlo no dice nada.
      showValidation: false,
      contratos:    [],
      companies:    [],
      planes:       [],
      productos:    [],
      lineasNegocio: [],
      ejecutivos:   [],
      brokers:      [],
      filtroPais:    '',
      countries:     [],
      netAmountDraft: null,
      filtroStatus:  '',
      filtroLinea:   '',
      filtroCompany: null,
      filtroContrato: null,
      form: emptyForm(),
      headers: [
        { text: 'ID',          value: 'codigo',       sortable: true },
        { text: 'Empresa',     value: 'company.name', sortable: true },
        { text: 'Broker',      value: 'canal',        sortable: true },
        { text: 'Producto',    value: 'producto',     sortable: false },
        { text: 'País',        value: 'pais',         sortable: false },
        { text: 'Línea',       value: 'lineaNegocio', sortable: true },
        { text: 'Estado',      value: 'status',       sortable: true },
        { text: 'Vencimiento', value: 'endDate',      sortable: true },
        { text: 'Ejecutivo',   value: 'executive',    sortable: false },
        { text: 'NPS',         value: 'npsSummary',   sortable: true },
        { text: '',            value: 'actions',      sortable: false, align: 'end' },
      ],
    };
  },

  computed: {
    brokerModalityItems() {
      return BROKER_MODALITY_ITEMS;
    },

    hiringModalityItems() {
      return HIRING_MODALITY_ITEMS;
    },

    /** La modalidad de contratación solo aplica a contratos Employee Benefit. */
    showsHiringModality() {
      return this.form.lineaNegocio === HIRING_MODALITY_LINE;
    },

    /** La modalidad solo aplica a contratos B2C vendidos por un broker. */
    showsBrokerModality() {
      return this.form.lineaNegocio === BROKER_MODALITY_LINE && this.form.canalTipo === 'Broker';
    },

    /**
     * Nombres de línea de negocio, cargados desde el mantenedor (no hardcodeados).
     *
     * Ordenados alfabéticamente y no por orden de creación del catálogo: alimentan tanto los
     * chips de filtro como el select del formulario, y ahí el orden de inserción no le dice
     * nada a quien busca una línea. `localeCompare` en vez de `sort()` a secas para que una
     * línea con acentos caiga donde corresponde.
     */
    lineas() {
      return this.lineasNegocio
        .map(l => l.nombre)
        .sort((a, b) => a.localeCompare(b, 'es'));
    },

    brokersActivos() {
      return this.brokers.filter(b => b.visible !== false);
    },

    /** Un descuento es un número entre 0 y 100; vacío no es lo mismo que 0. */
    descuentoRules() {
      return [
        v => (v !== null && v !== undefined && v !== '') || 'Requerido',
        v => (Number(v) >= 0 && Number(v) <= 100) || 'Entre 0 y 100',
      ];
    },

    /**
     * Descuentos en porcentaje para el formulario.
     *
     * En base se guardan como fracción (0,2 = 20 %) y el backend los usa tal cual en
     * `precio * (1 - descuento)`, así que la conversión vive solo acá: el campo se escribe
     * en porcentaje y el modelo sigue guardando la fracción. Cambiar lo almacenado obligaría
     * a migrar todos los contratos y a tocar cada consumidor.
     *
     * Se redondea al leer porque la fracción no siempre sobrevive el viaje: 0.2 * 100 da
     * 20.000000000000004 en coma flotante.
     */
    descuentoMensualPct: {
      get() {
        return fraccionAPorcentaje(this.form.descuentos?.monthlyDiscount);
      },
      set(val) {
        this.form.descuentos.monthlyDiscount = porcentajeAFraccion(val);
      },
    },

    descuentoAnualPct: {
      get() {
        return fraccionAPorcentaje(this.form.descuentos?.annualDiscount);
      },
      set(val) {
        this.form.descuentos.annualDiscount = porcentajeAFraccion(val);
      },
    },

    /**
     * Campos requeridos pendientes, agrupados por pestaña. Espeja las `rules` de
     * los campos del formulario: el v-form valida todas las pestañas a la vez
     * (van montadas con `eager`), pero solo se ven los errores de la pestaña
     * abierta, así que hace falta saber aparte en cuál quedó lo que falta.
     */
    tabErrors() {
      const f = this.form;
      const isUnitBilled = isUnitBilledLine(f.lineaNegocio);
      const vacio = v => v === null || v === undefined || v === '' || Number.isNaN(v);

      const general = [];
      if (!f.companyId) general.push('Empresa');
      if (!f.lineaNegocio) general.push('Línea de negocio');
      if (this.showsHiringModality && !f.hiringModality) general.push('Modalidad de contratación');
      if (!f.productId) general.push('Producto');
      if (!f.startDate) general.push('Fecha de inicio');
      if (this.isChurned && !f.endDate) general.push('Fecha de término');

      // Los descuentos se guardan como fracción: 1 es 100 %, no 1 %.
      const descuentoInvalido = v => vacio(v) || Number(v) < 0 || Number(v) > 1;

      const comercial = [];
      if (!f.tipoFacturacion) comercial.push('Tipo de facturación');
      if (!isUnitBilled && this.showNetAmount && vacio(f.netAmount)) comercial.push('Monto contrato');
      if (!isUnitBilled && descuentoInvalido(f.descuentos?.monthlyDiscount)) comercial.push('Desc. mensual (%)');
      if (!isUnitBilled && descuentoInvalido(f.descuentos?.annualDiscount)) comercial.push('Desc. anual (%)');

      const planes = [];
      if (f.canalTipo === 'Broker' && !f.brokerId) general.push('Broker');
      if (this.showsBrokerModality && !f.brokerModality) general.push('Modalidad de broker');
      if (!f.executiveId) general.push('Ejecutivo');
      if (!isUnitBilled && (f.domains || []).some(d => !d.domain)) general.push('Dominio');
      if (!isUnitBilled && (vacio(f.duracionMeses) || f.duracionMeses < 1)) planes.push('Duración (meses)');

      const facturacion = [];
      // 0 es un precio válido: hay contratos por unidad que se prestan sin cobro. Lo que no
      // se acepta es un precio negativo.
      if (isUnitBilled && (vacio(f.billingUnitPrice) || f.billingUnitPrice < 0)) {
        facturacion.push('Precio por unidad');
      }

      const comunicaciones = [];
      if (f.welcomeEmailTemplateId && !/^d-[0-9a-f]{32}$/.test(f.welcomeEmailTemplateId.trim())) {
        comunicaciones.push('Template de bienvenida');
      }

      return { general, comercial, planes, facturacion, 'facturacion-adicional': [], comunicaciones };
    },

    /** Pestañas del formulario: Comunicaciones solo si el producto manda correos. */
    formTabs() {
      return this.tabs.filter(t =>
        (t.value !== 'comunicaciones' || this.isUserClassifiedProduct) &&
        (t.value !== 'facturacion' || this.isUnitBilled) &&
        (t.value !== 'planes' || !this.isUnitBilled) &&
        (t.value !== 'facturacion-adicional' || !this.isUnitBilled));
    },

    /** Pestañas de la ficha de solo lectura, con la misma regla que el formulario. */
    viewTabs() {
      const sendsEmails = this.viewItem?.producto?.code === 'user';
      return this.tabs.filter(t =>
        (t.value !== 'comunicaciones' || sendsEmails) &&
        (t.value !== 'facturacion' || this.viewIsUnitBilled) &&
        (t.value !== 'planes' || !this.viewIsUnitBilled) &&
        (t.value !== 'facturacion-adicional' || !this.viewIsUnitBilled));
    },

    /** Pestañas con campos pendientes, en el orden en que se muestran. */
    tabsWithErrors() {
      return this.formTabs.filter(t => this.tabErrors[t.value].length > 0);
    },

    viewIsUnitBilled() {
      return isUnitBilledLine(this.viewItem?.lineaNegocio);
    },

    /** Moneda del contrato visto: la de su empresa, igual que en el formulario. */
    viewCurrency() {
      const company = this.companies.find(c => c.id === this.viewItem?.companyId);
      return company?.currency || this.viewItem?.netAmountCurrency || '—';
    },

    viewTipoContrato() {
      const item = TIPO_CONTRATO_ITEMS.find(t => t.value === this.viewItem?.tipoContrato);
      return item ? item.text : '—';
    },

    viewTabLabel() {
      return (this.tabs.find(t => t.value === this.viewTab)?.text || '').toLowerCase();
    },

    /**
     * Barra de vigencia de la ficha: tramo recorrido hasta hoy y lo que queda. Sin
     * vencimiento, lo que queda se dibuja punteado y con un largo fijo.
     */
    viewVigencia() {
      const start = this.viewItem?.startDate ? new Date(this.viewItem.startDate.slice(0, 10)) : null;
      const end = this.viewItem?.endDate ? new Date(this.viewItem.endDate.slice(0, 10)) : null;
      const now = new Date();
      if (!start) return { title: 'Sin fecha de inicio', filled: 0, rest: 1 };
      const months = (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth();
      let title;
      if (start > now) title = 'Todavía no comienza';
      else if (end && end < now) title = 'Vigencia terminada';
      else if (months < 1) title = 'Vigente desde este mes';
      else title = `Vigente desde hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
      if (!end) return { title, filled: start > now ? 0 : 3, rest: 2 };
      const total = Math.max(end - start, 1);
      const elapsed = Math.min(Math.max(now - start, 0), total);
      return { title, filled: elapsed / total, rest: 1 - elapsed / total };
    },

    /** Requeridos pendientes de todas las pestañas, para el panel de resumen. */
    summaryMissing() {
      return this.formTabs.flatMap(t => this.tabErrors[t.value]);
    },

    editingCodigo() {
      if (!this.editingId) return '';
      return this.contratos.find(c => c.id === this.editingId)?.codigo || '';
    },

    summaryCompanyName() {
      return this.companies.find(c => c.id === this.form.companyId)?.name || 'Empresa sin definir';
    },

    summarySubline() {
      const broker = this.form.canalTipo === 'Broker'
        ? this.brokers.find(b => b.id === this.form.brokerId)?.name || 'Broker'
        : this.form.canalTipo;
      const pais = this.paisDerived === '—' ? null : this.paisDerived;
      return [pais, this.monedaDerived, broker].filter(Boolean).join(' · ');
    },

    isEbContract() {
      return this.form.lineaNegocio === 'B2B Employee Benefit';
    },

    hasMembershipLimit() {
      const limit = this.form.membershipLimit;
      return limit !== null && limit !== '' && limit !== undefined;
    },

    /** Bolsa y tope en una línea, para dar contexto en la pestaña Planes. */
    coverageText() {
      const f = this.form;
      const parts = [];
      if (f.membershipQuota) parts.push(`${Number(f.membershipQuota).toLocaleString('es-CL')} membresías incluidas`);
      if (this.hasMembershipLimit) parts.push(`hasta ${f.membershipLimit} por colaborador`);
      const text = parts.join(' · ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    overQuotaTitle() {
      const quota = this.form.membershipQuota;
      return quota ? `Sobre las ${Number(quota).toLocaleString('es-CL')} membresías` : 'Sobre la bolsa';
    },

    overLimitCaption() {
      if (!this.hasMembershipLimit) return 'El contrato no tiene límite por colaborador, así que hoy nadie llega a esta situación.';
      return `Desde la ${Number(this.form.membershipLimit) + 1}.ª mascota, cuando pasa su límite.`;
    },

    /** Nombres de los planes activos del contrato, para la fila "mismo plan" de la mascota adicional. */
    contractPlanNames() {
      const names = this.form.planes
        .filter(p => p.planId && p.visible !== false)
        .map(p => this.planName(p.planId) || p.planName)
        .filter(Boolean);
      return names.length ? names.join(' · ') : 'Plan del contrato';
    },

    /**
     * El backend ofrece los planes B2C del país solo cuando el contrato no configura planes
     * propios para la mascota adicional; con alguno configurado, ofrece solo esos.
     */
    offersCountryB2c() {
      return !this.form.upsellPlans.some(u => u.planId);
    },

    /** Plan cuyo precio cobra el excedente de un contrato EB: el mismo que elige el MRR del backend. */
    summaryEbPlan() {
      const planes = this.form.planes.filter(p => Number(p.precioMensualNeto) > 0);
      return planes.find(p => p.type === 'eb') || planes[0] || null;
    },

    /** Extras configurados (monto > 0), en una línea; se suman al MRR. */
    summaryExtras() {
      const extras = [
        ['collectionFee', 'recaudación'],
        ['commission', 'comisión'],
        ['managementFee', 'gestión'],
      ]
        .filter(([key]) => Number(this.form[`${key}Amount`]) > 0)
        .map(([key, name]) => {
          const unit = this.form[`${key}Unit`];
          const amount = Number(this.form[`${key}Amount`]).toLocaleString('es-CL', { maximumFractionDigits: 4 });
          return unit === '%' ? `${amount} % ${name}` : `${amount} ${unit} ${name}`;
        });
      return extras.join(' · ');
    },

    /**
     * MRR estimado con la misma regla que MRR Detalle en el backend: TPA = reembolsos del mes
     * × precio por unidad; EB y monto fijo = el monto (÷ 12 si es anual); el resto depende de
     * las membresías activas de cada mes, que el formulario no conoce.
     */
    summaryMrr() {
      const f = this.form;
      if (this.isUnitBilled) {
        const hasPrice = f.billingUnitPrice !== null && f.billingUnitPrice !== '' && !Number.isNaN(f.billingUnitPrice);
        if (!hasPrice) {
          return { hasValue: false, value: '—', suffix: '', caption: 'Define el precio por unidad para estimarlo. Cada mes se multiplica por los reembolsos que llegan.' };
        }
        return {
          hasValue: true,
          value: this.formatMoney(f.billingUnitPrice, f.billingCurrency),
          suffix: '× reembolsos del mes',
          caption: 'Se estima en vivo con los reembolsos que llegan cada mes, en cualquier estado, hasta que se genera la facturación.',
        };
      }
      if (this.showNetAmount) {
        if (f.netAmount === null || f.netAmount === '') {
          return { hasValue: false, value: '—', suffix: '', caption: 'Define el monto del contrato para estimarlo.' };
        }
        const currency = this.netAmountCurrencyLabel;
        const isAnnual = f.tipoFacturacion === 'Anual';
        const parts = [isAnnual ? `${this.formatMoney(f.netAmount, currency)} anual ÷ 12` : 'Monto mensual del contrato'];
        if (f.lineaNegocio === 'B2B Employee Benefit') {
          if (f.membershipQuota) parts.push(`incluye ${Number(f.membershipQuota).toLocaleString('es-CL')} membresías`);
          const plan = this.summaryEbPlan;
          if (plan) parts.push(`la empresa paga ${this.formatMoney(plan.precioMensualNeto, plan.monedaPlan)} más por cada membresía activa sobre la bolsa`);
        }
        if (this.summaryExtras) parts.push('más los extras');
        return {
          hasValue: true,
          value: this.formatMoney(isAnnual ? Math.round(f.netAmount / 12) : f.netAmount, currency),
          suffix: '',
          caption: `${parts.join(', ')}.`,
        };
      }
      return { hasValue: false, value: '—', suffix: '', caption: 'Depende de las membresías activas de cada mes y del precio de sus planes.' };
    },

    /** Meses que la facturación por unidad generaría hoy: del inicio al mes en curso (o al vencimiento). */
    summaryUnitBilledMonths() {
      if (!this.form.startDate) return null;
      const toKey = iso => {
        const [y, m] = iso.slice(0, 10).split('-').map(Number);
        return y * 12 + m;
      };
      const now = new Date();
      let last = now.getFullYear() * 12 + now.getMonth() + 1;
      if (this.form.endDate) last = Math.min(last, toKey(this.form.endDate));
      return Math.max(0, last - toKey(this.form.startDate) + 1);
    },

    summaryFacts() {
      const f = this.form;
      const toDmy = iso => (iso ? iso.slice(0, 10).split('-').reverse().join('-') : '');
      const facts = [];
      if (this.isUnitBilled) {
        facts.push({ label: 'Precio por unidad', value: this.summaryMrr.hasValue ? this.summaryMrr.value : '—' });
        facts.push({ label: 'Cuentan', value: 'Reembolsos de servicios asignados' });
        if (f.status !== 'Activo') {
          facts.push({ label: 'Se factura', value: 'No: contrato no Activo', warn: true });
        } else if (this.summaryUnitBilledMonths !== null) {
          facts.push({ label: 'Meses por generar', value: String(this.summaryUnitBilledMonths) });
        }
      } else {
        const plan = this.summaryEbPlan;
        const monthly = fraccionAPorcentaje(f.descuentos?.monthlyDiscount) || 0;
        const annual = fraccionAPorcentaje(f.descuentos?.annualDiscount) || 0;
        facts.push({ label: 'Planes activos', value: String(f.planes.filter(p => p.planId && p.visible !== false).length) });
        if (this.isEbContract && plan) {
          facts.push({ label: 'Sobre la bolsa', value: `${this.formatMoney(plan.precioMensualNeto, plan.monedaPlan)} c/u · paga la empresa` });
        }
        facts.push({ label: 'Dominios', value: String(f.domains.filter(d => d.domain).length) });
        if (f.membershipQuota) {
          facts.push({ label: 'Membresías contratadas', value: Number(f.membershipQuota).toLocaleString('es-CL') });
        }
        facts.push({ label: 'Renovación', value: this.membershipRenewalModeLabel(f.membershipRenewalMode) });
        facts.push({ label: 'Límite por colaborador', value: this.hasMembershipLimit ? String(f.membershipLimit) : 'Sin límite' });
        facts.push({ label: 'Descuentos', value: monthly || annual ? `Mensual ${monthly} % · Anual ${annual} %` : 'Sin descuentos' });
        facts.push({ label: 'Extras', value: this.summaryExtras || 'Sin extras' });
        const upsellCount = f.upsellPlans.filter(u => u.planId).length;
        const offered = upsellCount ? `${upsellCount} ${upsellCount === 1 ? 'plan' : 'planes'}` : 'Planes B2C';
        facts.push({ label: 'Pasa su límite', value: `${offered} · paga el colaborador` });
        facts.push({ label: 'Mascotas adicionales en App', value: f.allowAdditionalPetFromApp !== false ? 'Permitido' : 'Bloqueado' });
        facts.push({ label: 'Edad bloqueante', value: f.ageBlocksContracting === true ? 'Sí' : 'No' });
      }
      if (f.lineaNegocio === COUNTRY_DEFAULT_LINE) {
        facts.push({ label: 'Default del país', value: f.isCountryDefault ? 'Sí' : 'No' });
      }
      facts.push({ label: 'Cobro', value: f.tipoFacturacion || '—' });
      facts.push({ label: 'Inicio', value: toDmy(f.startDate) || '—' });
      facts.push({ label: 'Vence', value: toDmy(f.endDate) || 'Sin vencimiento' });
      const executive = this.ejecutivos.find(e => e.id === f.executiveId);
      facts.push({ label: 'Ejecutivo', value: executive ? `${executive.name} ${executive.surname}` : '—' });
      return facts;
    },

    /** El monto admite centavos salvo en CLP. */
    netAmountAllowsDecimals() {
      return this.netAmountCurrencyLabel !== 'CLP';
    },

    /**
     * Monto contrato mostrado con separador de miles; guarda número en form.netAmount.
     * Mientras se escribe se devuelve el texto tal cual (`netAmountDraft`): si se
     * reformateara en cada tecla, la coma de "24," desaparecería antes de poder
     * escribir el decimal.
     */
    netAmountDisplay: {
      get() {
        const allowDecimals = this.netAmountAllowsDecimals;
        if (
          this.netAmountDraft !== null &&
          CurrencyUtils.parseAmountInput(this.netAmountDraft, allowDecimals) === (this.form.netAmount ?? null)
        ) {
          return this.netAmountDraft;
        }
        if (this.form.netAmount == null || this.form.netAmount === '') return '';
        return Number(this.form.netAmount).toLocaleString('es-CL', {
          maximumFractionDigits: allowDecimals ? 2 : 0,
        });
      },
      set(val) {
        this.netAmountDraft = String(val ?? '');
        this.form.netAmount = CurrencyUtils.parseAmountInput(val, this.netAmountAllowsDecimals);
      },
    },

    ebProductoNombre() {
      const prod = this.productos.find(p =>
        p.nombre && p.nombre.toLowerCase().includes('employee benefit')
      );
      return prod ? prod.nombre : '—';
    },

    /**
     * Nombre del producto del contrato. En Employee Benefit el id lo fija la línea
     * de negocio, y un contrato viejo puede haber quedado guardado sin él: ahí se
     * cae al producto EB del catálogo en vez de mostrar el campo vacío.
     */
    productoNombre() {
      const prod = this.productos.find(p => p.id === this.form.productId);
      if (prod?.nombre) return prod.nombre;
      return this.form.lineaNegocio === 'B2B Employee Benefit' ? this.ebProductoNombre : '—';
    },

    /** La línea elegida factura por unidad (TPA). */
    isUnitBilled() {
      return isUnitBilledLine(this.form.lineaNegocio);
    },

    /**
     * Productos disponibles para la línea de negocio seleccionada.
     *
     * Cada línea por unidad ofrece solo el suyo; el resto de las líneas ofrece todo lo que
     * no sea de una línea por unidad. Se filtra por `producto.code`, no por nombre, porque
     * el nombre se puede renombrar libremente (ej. "TPA" -> "Claims").
     */
    productosFiltrados() {
      const propios = UNIT_BILLED_PRODUCT_CODES_BY_LINE[this.form.lineaNegocio];
      if (propios) return this.productos.filter(p => propios.includes(p.code));

      const porUnidad = UNIT_BILLED_LINES
        .reduce((acc, l) => acc.concat(UNIT_BILLED_PRODUCT_CODES_BY_LINE[l]), []);
      return this.productos.filter(p => !porUnidad.includes(p.code));
    },


    /** Código del producto seleccionado en el formulario, en cualquier línea de negocio (o null). */
    selectedProductoCode() {
      const prod = this.productos.find(p => p.id === this.form.productId);
      return prod?.code ?? null;
    },

    /** El producto seleccionado está clasificado como "user": da de alta usuarios finales. */
    isUserClassifiedProduct() {
      return this.selectedProductoCode === 'user';
    },

    opcionesPais() {
      return [{ text: 'Todos', value: '' }, ...this.countries.map(c => ({ text: c.name, value: c.name }))];
    },

    opcionesEstado() {
      return [{ text: 'Todos', value: '' }, ...this.contractStatuses.map(s => ({ text: s, value: s }))];
    },

    opcionesLinea() {
      return [{ text: 'Todas', value: '' }, ...this.lineas.map(l => ({ text: l, value: l }))];
    },

    /** Contratos que pasan los demás filtros: el buscador de contrato se acota con ellos. */
    contratosFiltradosSinContrato() {
      return this.contratos
        // Por ISO y no por nombre: la base tuvo "Mexico" sin tilde hasta la spec 084.
        .filter(c => !this.filtroPais    || CurrencyUtils.countryIso(c.company?.country?.name) === CurrencyUtils.countryIso(this.filtroPais))
        .filter(c => !this.filtroStatus  || c.status === this.filtroStatus)
        .filter(c => !this.filtroLinea   || c.lineaNegocio === this.filtroLinea)
        .filter(c => !this.filtroCompany || c.companyId === this.filtroCompany);
    },

    contratosDisponibles() {
      return this.contratosFiltradosSinContrato
        .map(c => ({ text: `${c.codigo} · ${c.company?.name || 'Sin empresa'}`, value: c.id }))
        .sort((a, b) => a.text.localeCompare(b.text, 'es'));
    },

    filteredContratos() {
      return this.contratosFiltradosSinContrato
        .filter(c => !this.filtroContrato || c.id === this.filtroContrato);
    },

    /** País derivado de la empresa seleccionada en el formulario */
    /** Más reciente arriba: es lo que uno busca al abrir la ficha. */
    statusHistoryDesc() {
      return [...this.statusHistory].reverse();
    },

    tipoContratoHint() {
      return TIPO_CONTRATO_HINTS[this.form.tipoContrato] || '';
    },

    isFixedAmount() {
      return this.form.billingBasis === 'fixed_amount';
    },

    /**
     * El monto dejó de ser exclusivo de Employee Benefit: un contrato de monto
     * fijo se define justamente por esa cifra.
     */
    showNetAmount() {
      return (
        this.form.lineaNegocio === 'B2B Employee Benefit' || this.isFixedAmount
      );
    },

    netAmountCurrencyLabel() {
      return this.form.netAmountCurrency || this.monedaDerived;
    },

    isChurned() {
      return this.form.status === 'Churned';
    },

    /**
     * La fecha de término solo es obligatoria al dar de baja. Un contrato
     * indefinido vigente no tiene por qué tenerla — los 44 activos sin fecha
     * son legítimos; lo contradictorio es decir que un cliente se fue sin
     * decir cuándo.
     */
    endDateRules() {
      if (!this.isChurned) return [];
      return [v => !!v || 'Requerida al marcar el contrato como churned'];
    },

    paisDerived() {
      if (!this.form.companyId) return '—';
      const company = this.companies.find(c => c.id === this.form.companyId);
      if (company?.countryName) return company.countryName;
      // Fallback: buscar en contratos existentes
      const contrato = this.contratos.find(c => c.companyId === this.form.companyId);
      return contrato?.company?.country?.name || '—';
    },

    /** Moneda derivada directamente del campo currency de la empresa/país */
    monedaDerived() {
      if (!this.form.companyId) return 'CLP';
      const company = this.companies.find(c => c.id === this.form.companyId);
      // Usar currency directo del endpoint (más confiable que derivar del nombre)
      if (company?.currency) return company.currency;
      // Fallback: derivar desde nombre del país en contratos existentes
      const normalize = s => s.toLowerCase().normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
      const name = normalize(this.paisDerived || '');
      if (name.includes('chile')) return 'CLP';
      if (name.includes('peru'))  return 'PEN';
      if (name.includes('mexico')) return 'MXN';
      return 'CLP';
    },

    /** Planes del detalle con los activos primero (sort estable: cada grupo mantiene su orden). */
    viewPlanRows() {
      return [...((this.viewItem && this.viewItem.planes) || [])]
        .sort((a, b) => Number(b.visible !== false) - Number(a.visible !== false));
    },

    viewActivePlanCount() {
      return this.viewPlanRows.filter(cp => cp.visible !== false).length;
    },

    /** Planes inactivos que siguen colapsados (los que ya venían así al abrir). */
    collapsedInactiveCount() {
      return this.form.planes.filter(p => this.isPlanCollapsed(p)).length;
    },

    /**
     * Filas del formulario de planes: primero las visibles y al final las
     * colapsadas, marcando la primera para poner ahí el botón que las muestra.
     * `idx` es la posición real en form.planes (etiqueta y acciones usan esa).
     */
    planRows() {
      const rows = this.form.planes.map((item, idx) => ({ item, idx, collapsed: this.isPlanCollapsed(item) }));
      const ordered = [...rows.filter(r => !r.collapsed), ...rows.filter(r => r.collapsed)];
      return ordered.map((r, i) => ({ ...r, firstCollapsed: r.collapsed && (i === 0 || !ordered[i - 1].collapsed) }));
    },

    /** IVA rate by country for net price calculation */
    ivaRate() {
      const p = (this.paisDerived || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      if (p.includes('peru')) return 18;
      if (p.includes('mexico')) return 16;
      return 19;
    },

    /** Hint de MRR mensual para el campo Monto Contrato */
    mrrHint() {
      if (this.form.netAmount == null || this.form.netAmount === '') return '';
      const mrr = this.form.tipoFacturacion === 'Anual'
        ? Math.round((this.form.netAmount / 12) * 100) / 100
        : this.form.netAmount;
      const decimals = this.netAmountAllowsDecimals ? 2 : 0;
      // La moneda del contrato, no la del país: CL-245 está en USD y el aviso decía CLP.
      return `MRR mensual: ${mrr.toLocaleString('es-CL', { maximumFractionDigits: decimals })} ${this.netAmountCurrencyLabel}`;
    },
  },

  watch: {
    // Si cambia el producto y la pestaña Comunicaciones deja de existir, no quedar en blanco.
    isUserClassifiedProduct(isUser) {
      if (!isUser && this.activeTab === 'comunicaciones') this.activeTab = 'general';
    },
    isUnitBilled(unitBilled) {
      if (unitBilled && this.activeTab === 'facturacion-adicional') this.activeTab = 'general';
      if (!unitBilled && this.activeTab === 'facturacion') this.activeTab = 'general';
      if (unitBilled && this.activeTab === 'planes') this.activeTab = 'general';
    },

    monedaDerived(newMoneda) {
      (this.form.planes || []).forEach(p => { p.monedaPlan = newMoneda; });

      // Solo se reajusta la unidad de un extra que sigue en 0, o sea sin configurar: si
      // alguien ya puso "2 %", cambiar de empresa no debe pisarle la unidad.
      for (const extra of ['collectionFee', 'commission', 'managementFee']) {
        if (!Number(this.form[`${extra}Amount`])) {
          this.form[`${extra}Unit`] = newMoneda;
        }
      }
    },
  },

  async mounted() {
    if (this.$route.query.companyId) {
      this.filtroCompany = this.$route.query.companyId;
    }
    await this.loadAll();
    // Llegada desde Renovaciones: se abre directo la edición del contrato para
    // cargar la fecha de término que falta, sin buscarlo a mano en la lista.
    // Con `mode=view` (llegada desde el detalle de un plan) se abre solo la ficha,
    // para no exponer la edición a quien solo quería consultar el contrato.
    const codigo = this.$route.query.codigo;
    if (codigo) {
      const contrato = (this.contratos || []).find(c => c.codigo === codigo);
      // Si no se encuentra, se deja la lista completa: esta vista no tiene
      // snackbar propio y abrir un diálogo vacío sería peor que no abrir nada.
      if (contrato && this.$route.query.mode === 'view') this.openView(contrato);
      else if (contrato) this.openEdit(contrato);
    }
  },

  methods: {
    async loadAll() {
      this.loading = true;
      const [contRes, prodRes, compRes, planRes, brokerRes, ejRes, lineaRes] = await Promise.all([
        ContratoService.getAll(),
        ProductoService.getAll(),
        CompanyService.getCompaniesWithCountry(),
        PlansService.getPlans(1, 500, ''),
        BrokerService.getAll(),
        EjecutivosService.getAll(),
        LineaNegocioService.getAll(),
      ]);
      if (contRes.status === 'ok')    this.contratos = contRes.data;
      if (prodRes.status === 'ok')    this.productos = prodRes.data;
      if (compRes.status === 'ok')    this.companies = (compRes.data || []).sort((a, b) => a.name.localeCompare(b.name));
      if (planRes.status === 'ok') {
        this.planes = (planRes.data?.plans || planRes.data || []).filter(p => p.visible !== false);
      }
      if (brokerRes.status === 'ok')  this.brokers    = brokerRes.data;
      if (ejRes.status === 'ok')      this.ejecutivos = ejRes.data;
      if (lineaRes.status === 'ok')   this.lineasNegocio = lineaRes.data;
      this.loading = false;
      await this.loadCountries();
    },

    /** Países visibles para el filtro; si fallan, el filtro queda solo con "Todos". */
    async loadCountries() {
      try {
        const res = await CountryService.getAllCountries(true);
        if (res.status === 'ok') this.countries = res.data || [];
      } catch (e) {
        console.error('[Contratos.loadCountries]', e);
      }
    },

    /** Monto con el símbolo de su moneda; UF conserva hasta 4 decimales. */
    formatMoney(amount, currency) {
      const text = (Number(amount) || 0).toLocaleString('es-CL', { maximumFractionDigits: currency === 'UF' ? 4 : 2 });
      if (currency === 'CLP') return `$${text}`;
      if (currency === 'PEN') return `S/ ${text}`;
      if (currency === 'USD') return `US$ ${text}`;
      return `${text} ${currency || ''}`.trim();
    },

    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.collapseInactivePlans();
      this.netAmountDraft = null;
      // El formulario arranca en Employee Benefit sin pasar por el select, así que
      // sin esto el contrato se creaba sin productId: la línea nunca "cambiaba".
      this.onLineaChange(this.form.lineaNegocio);
      this.error = null;
      this.resetTabs();
      this.dialog = true;
    },

    /** Vuelve a la primera pestaña y esconde los contadores de error. */
    resetTabs() {
      this.activeTab = 'general';
      this.showValidation = false;
    },

    openView(item) {
      this.viewItem = item;
      this.viewTab = 'general';
      this.showViewHistory = false;
      this.showViewInactivePlans = false;
      this.viewDialog = true;
      this.loadStatusHistory(item.id);
    },

    /** Abre la edición en la misma pestaña que se estaba viendo. */
    editFromView() {
      const tab = this.viewTab;
      this.openEdit(this.viewItem);
      this.activeTab = tab;
      this.viewDialog = false;
    },

    /**
     * El historial se pide al abrir el contrato y no con el listado: son
     * decenas de contratos y casi nunca se mira el de todos.
     */
    async loadStatusHistory(contratoId) {
      this.statusHistory = [];
      this.loadingHistory = true;
      const res = await ContratoService.getStatusHistory(contratoId);
      this.loadingHistory = false;
      // Un fallo deja el historial vacío con su mensaje: no vale interrumpir
      // la ficha del contrato porque no se pudo traer la traza.
      if (res.status === 'ok') this.statusHistory = res.data || [];
    },

    formatHistoryDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    endReasonLabel(reason) {
      return END_REASON_LABELS[reason] || reason;
    },

    historyColor(status) {
      if (status === 'Activo') return 'green darken-1';
      if (status === 'Pausado') return 'orange darken-1';
      return 'grey darken-1';
    },

    openEdit(item) {
      this.editingId = item.id;

      // Detectar si el canal guardado corresponde a un broker
      const brokerMatch = this.brokers.find(
        b => b.name === item.canal || b.bussinesName === item.canal
      );
      const canalTipo = brokerMatch ? 'Broker' : 'Directo';
      const brokerId  = brokerMatch ? brokerMatch.id : null;

      // Build planes list from contrato_planes; fallback to legacy planId
      let planes = [];
      if (item.planes && item.planes.length > 0) {
        planes = item.planes.map(cp => ({
          planId: cp.planId,
          precioMensualNeto: cp.precioMensualNeto ?? 0,
          monedaPlan: cp.monedaPlan || 'CLP',
          visible: cp.visible !== false,
          type: cp.type ?? null,
          planName: cp.plan?.name || null,
          planVisible: cp.plan?.visible !== false,
          planMonthlyPrice: cp.plan?.monthlyPrice ?? null,
          planAnnualPrice: cp.plan?.annualPrice ?? null,
          planCurrency: cp.plan?.currency || null,
        }));
      } else if (item.planId) {
        planes = [{ planId: item.planId, precioMensualNeto: item.netAmount ?? 0, monedaPlan: 'CLP', type: null }];
      }

      // Auto-fill productId for B2B Employee Benefit if not set
      let productId = item.productId;
      if (!productId && item.lineaNegocio === 'B2B Employee Benefit') {
        const prod = this.productos.find(p =>
          p.nombre && p.nombre.toLowerCase().includes('employee benefit')
        );
        productId = prod?.id ?? null;
      }

      this.netAmountDraft = null;
      this.form = {
        companyId:        item.companyId,
        planes,
        productId,
        executiveId:      item.executiveId,
        lineaNegocio:     item.lineaNegocio,
        canalTipo,
        brokerId,
        brokerModality:   item.brokerModality ?? null,
        hiringModality:   item.hiringModality ?? null,
        canal:            item.canal || 'Directo',
        tipoFacturacion:  item.tipoFacturacion,
        netAmount:        item.netAmount,
        netAmountCurrency: item.netAmountCurrency ?? null,
        membershipQuota:  item.membershipQuota || null,
        membershipLimit:  item.membershipLimit ?? null,
        billingBasis:     item.billingBasis ?? null,
        tipoContrato:     item.tipoContrato ?? 'comercial',
        membershipRenewalMode: item.membershipRenewalMode ?? 'none',
        duracionMeses:    item.duracionMeses ?? 12,
        startDate:        item.startDate?.slice(0, 10) || '',
        endDate:          item.endDate?.slice(0, 10)   || '',
        status:           item.status,
        domains:     (item.domains || []).map(d => ({ domain: d.domain })),
        billingUnitPrice:       item.billingTerms?.unitPrice ?? null,
        billingCurrency:        item.billingTerms?.currency || 'CLP',
        collectionFeeAmount:    item.collectionFeeAmount ?? null,
        collectionFeeUnit:      item.collectionFeeUnit ?? null,
        commissionAmount:       item.commissionAmount ?? null,
        commissionUnit:         item.commissionUnit ?? null,
        managementFeeAmount:    item.managementFeeAmount ?? null,
        managementFeeUnit:      item.managementFeeUnit ?? null,
        sendWelcomeEmail:               item.sendWelcomeEmail ?? true,
        sendContractAssignedEmail:      item.sendContractAssignedEmail ?? true,
        sendMembershipActivatedEmail:   item.sendMembershipActivatedEmail ?? true,
        sendMembershipDeactivatedEmail: item.sendMembershipDeactivatedEmail ?? true,
        welcomeEmailTemplateId:         item.welcomeEmailTemplateId ?? null,
        isCountryDefault:               item.isCountryDefault ?? false,
        allowAdditionalPetFromApp:      item.allowAdditionalPetFromApp ?? true,
        ageBlocksContracting:           item.ageBlocksContracting ?? false,
        upsellPlans: (item.upsellPlans || []).map(u => ({
          planId:            u.planId,
          trigger:           u.trigger,
          type:              u.type || null,
          monedaPlan:        u.monedaPlan || null,
          precioMensualNeto: u.precioMensualNeto ?? 0,
        })),
        // Los descuentos son parte fija del contrato: un contrato sin fila de
        // paymentModes es 0 %, no "sin descuentos cargados".
        descuentos: {
          monthlyDiscount: item.paymentModes?.[0]?.monthlyDiscount ?? 0,
          annualDiscount:  item.paymentModes?.[0]?.annualDiscount ?? 0,
        },
      };
      this.error = null;
      this.collapseInactivePlans();
      this.resetTabs();
      this.dialog = true;
    },

    /**
     * Al marcar la baja se precarga hoy, pero queda editable: la fecha que
     * importa es cuándo terminó el servicio, no cuándo se registró, y suelen
     * ser distintas. De esa fecha depende en qué mes cae la baja en el MRR.
     */
    onStatusChange(status) {
      if (status === 'Churned' && !this.form.endDate) {
        this.form.endDate = new Date().toISOString().slice(0, 10);
      }
    },

    onLineaChange(val) {
      if (val === 'B2B Employee Benefit') {
        const prod = this.productos.find(p =>
          p.nombre?.toLowerCase().includes('employee benefit')
        );
        this.form.productId = prod?.id ?? null;
      } else {
        this.form.productId = null;
      }

      // La modalidad de contratación es solo de Employee Benefit.
      if (val !== HIRING_MODALITY_LINE) this.form.hiringModality = null;

      // Membresías contratadas solo aplica a EB y B2B Asistencias; al cambiar
      // a otra línea se limpia para no dejar un valor stale sin forma de editarlo.
      if (!['B2B Employee Benefit', 'B2B Asistencias'].includes(val)) {
        this.form.membershipQuota = null;
      }

      // B2C: condiciones comerciales por defecto
      if (val === 'B2C') {
        this.form.billingBasis = 'users_in_contract';
        this.form.membershipLimit = 1000;
      }

      // Las líneas por unidad no usan descuentos, planes ni dominios: se limpian al entrar.
      // billingBasis se limpia también: la deriva el producto (ver onProductoChange).
      if (isUnitBilledLine(val)) {
        this.form.descuentos = { monthlyDiscount: 0, annualDiscount: 0 };
        this.form.planes = [];
        this.form.domains = [];
        this.form.billingBasis = null;
      }
    },

    /** Deriva "Se factura por" del producto elegido (no es una elección libre). */
    onProductoChange(productId) {
      if (!this.isUnitBilled) return;
      const prod = this.productos.find(p => p.id === productId);
      this.form.billingBasis = UNIT_BILLING_BASIS_BY_PRODUCT_CODE[prod?.code] || null;
    },

    /** Colapsa los planes que ya están inactivos al abrir el formulario. */
    collapseInactivePlans() {
      this.collapsedPlans = this.form.planes.filter(p => p.visible === false);
      this.showInactivePlans = false;
    },

    /** Inactivo desde que se abrió el formulario (no uno que se apagó al editar). */
    isPlanCollapsed(item) {
      return item.visible === false && this.collapsedPlans.includes(item);
    },

    addPlan() {
      this.form.planes.push({ planId: null, precioMensualNeto: 0, monedaPlan: this.monedaDerived || 'CLP', visible: true, type: 'b2c' });
    },

    askRemovePlan(idx) {
      this.removePlanIdx = idx;
      this.removePlanDialog = true;
    },

    confirmRemovePlan() {
      if (this.removePlanIdx !== null) {
        this.form.planes.splice(this.removePlanIdx, 1);
      }
      this.removePlanDialog = false;
      this.removePlanIdx = null;
    },

    planesConActual(idx) {
      const item = this.form.planes[idx];
      if (!item.planId || this.planes.find(p => p.id === item.planId)) return this.planes;
      return [...this.planes, { id: item.planId, name: item.planName || item.planId }];
    },

    onPlanChange(idx) {
      const item = this.form.planes[idx];
      if (!item.planId) item.precioMensualNeto = 0;
    },

    calcularPrecioMensual(idx) {
      const item = this.form.planes[idx];
      const plan = this.planes.find(p => p.id === item.planId);
      if (!plan || !plan.monthlyPrice) return 0;
      const discount = this.form.descuentos?.monthlyDiscount || 0;
      const result = plan.monthlyPrice * (1 - discount) / (1 + this.ivaRate / 100);
      const isDecimal = (plan.currency || '').toUpperCase() === 'UF' || result < 100;
      return isDecimal
        ? Math.round(result * 10000) / 10000
        : Math.round(result);
    },

    aplicarCalculoPrecio(idx) {
      const item = this.form.planes[idx];
      if (!item.planId) return;
      item.precioMensualNeto = this.calcularPrecioMensual(idx);
    },

    limpiarFiltros() {
      this.filtroPais = '';
      this.filtroStatus = '';
      this.filtroLinea = '';
      this.filtroContrato = null;
      // Si se llegó filtrado por empresa (desde otra vista), también se suelta.
      this.filtroCompany = null;
    },

    onCanalTipoChange(val) {
      if (val === 'Directo') {
        this.form.brokerId = null;
        this.form.brokerModality = null;
      }
    },

    hiringModalityLabel(value) {
      return (HIRING_MODALITY_ITEMS.find(m => m.value === value) || {}).text || value;
    },

    brokerModalityLabel(value) {
      return (BROKER_MODALITY_ITEMS.find(m => m.value === value) || {}).text || value;
    },

    membershipRenewalModeLabel(value) {
      return (MEMBERSHIP_RENEWAL_MODE_ITEMS.find(m => m.value === (value || 'none')) || {}).text || value;
    },

    addUpsellPlan() {
      this.form.upsellPlans.push({
        planId: null,
        trigger: 'additional_pet',
        type: 'b2c',
        monedaPlan: this.monedaDerived || 'CLP',
        precioMensualNeto: 0,
      });
    },

    removeUpsellPlan(idx) {
      this.form.upsellPlans.splice(idx, 1);
    },

    /**
     * Planes elegibles para una fila de upsell: ni los que el contrato ya cubre (un plan no
     * puede ser cubierto y de upsell a la vez) ni los elegidos en otras filas de upsell.
     */
    upsellPlanOptions(idx) {
      const taken = new Set([
        ...this.form.planes.map(p => p.planId),
        ...this.form.upsellPlans.filter((_, i) => i !== idx).map(u => u.planId),
      ]);
      return this.planes.filter(p => !taken.has(p.id));
    },

    addDomain() {
      this.form.domains.push({ domain: '' });
    },

    askRemoveDomain(idx) {
      this.removeDomainIdx = idx;
      this.removeDomainDialog = true;
    },

    confirmRemoveDomain() {
      if (this.removeDomainIdx !== null) {
        this.form.domains.splice(this.removeDomainIdx, 1);
      }
      this.removeDomainDialog = false;
      this.removeDomainIdx = null;
    },

    closeDialog() {
      this.dialog = false;
      this.error = null;
      this.resetTabs();
      this.$refs.form?.resetValidation();
    },

    openDeleteDialog(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      const res = await ContratoService.delete(this.deleteTarget.id);
      this.deleting = false;
      if (res.status === 'ok') {
        this.contratos = this.contratos.filter(c => c.id !== this.deleteTarget.id);
        this.deleteDialog = false;
        this.deleteTarget = null;
      } else {
        this.error = 'Ocurrió un error eliminando el contrato, intenta más tarde.';
        this.deleteDialog = false;
      }
    },

    /**
     * Moves the flat unit-billing fields of `payload` into `billingTerms`
     * (contrato-billing-terms), deleting the flat fields either way.
     */
    applyBillingTerms(payload) {
      if (this.isUnitBilled) {
        payload.billingTerms = {
          unitPrice: payload.billingUnitPrice,
          currency: payload.billingCurrency || 'CLP',
        };
      }
      delete payload.billingUnitPrice;
      delete payload.billingCurrency;
    },

    /**
     * Moves the flat extras fields of `payload` (por recaudación / por comisión / por
     * gestión) into `extras`, applicable to a contract of any business line — unlike
     * `billingTerms`, which is TPA-only.
     *
     * No aplica a las líneas facturadas por unidad (TPA): esos contratos cobran por
     * unidad consumida y no llevan extras.
     */
    applyExtras(payload) {
      // Las líneas por unidad no cobran extras: no se envía `extras` en absoluto, para no
      // pisar ni limpiar nada en el backend.
      if (!this.isUnitBilled) {
        payload.extras = {
          collectionFeeAmount: payload.collectionFeeAmount ?? null,
          collectionFeeUnit: payload.collectionFeeAmount != null ? payload.collectionFeeUnit : null,
          commissionAmount: payload.commissionAmount ?? null,
          commissionUnit: payload.commissionAmount != null ? payload.commissionUnit : null,
          managementFeeAmount: payload.managementFeeAmount ?? null,
          managementFeeUnit: payload.managementFeeAmount != null ? payload.managementFeeUnit : null,
        };
      }
      delete payload.collectionFeeAmount;
      delete payload.collectionFeeUnit;
      delete payload.commissionAmount;
      delete payload.commissionUnit;
      delete payload.managementFeeAmount;
      delete payload.managementFeeUnit;
    },

    buildPayload() {
      const payload = { ...this.form };

      // El form trabaja con el nombre (para no reescribir cada comparación por
      // línea ya existente); el backend espera el id de lineas-negocio.
      const linea = this.lineasNegocio.find(l => l.nombre === payload.lineaNegocio);
      payload.businessLineId = linea ? linea.id : null;
      // Solo un contrato B2C puede ser el default del país (el backend lo rechaza igual).
      payload.isCountryDefault = payload.lineaNegocio === COUNTRY_DEFAULT_LINE && !!payload.isCountryDefault;
      delete payload.lineaNegocio;

      if (payload.canalTipo === 'Broker' && payload.brokerId) {
        const broker  = this.brokers.find(b => b.id === payload.brokerId);
        payload.canal = broker ? broker.name : 'Broker';
      } else {
        payload.canal = 'Directo';
      }

      // Fuera de un contrato B2C de broker la modalidad se manda en null: el backend la
      // borra igual, pero así el formulario no arrastra una elección que ya no aplica.
      if (!this.showsBrokerModality) payload.brokerModality = null;
      // Igual con la modalidad de contratación fuera de Employee Benefit.
      if (!this.showsHiringModality) payload.hiringModality = null;

      delete payload.canalTipo;
      delete payload.brokerId;

      // endDate: normalize empty/null → null so the backend can explicitly clear it
      payload.endDate = payload.endDate || null;
      if (!payload.productId)       delete payload.productId;
      if (!payload.executiveId)     delete payload.executiveId;

      // membershipQuota: null explícito (no delete) cuando se deja vacío, para
      // que el backend pueda limpiar un valor previamente cargado (= sin límite).
      payload.membershipQuota =
        payload.membershipQuota === '' || payload.membershipQuota == null || Number.isNaN(payload.membershipQuota)
          ? null
          : Math.max(1, Math.trunc(payload.membershipQuota));

      // membershipLimit / billingBasis: send null (not undefined) so el backend pueda limpiarlos
      payload.membershipLimit =
        payload.membershipLimit === '' || payload.membershipLimit == null || Number.isNaN(payload.membershipLimit)
          ? null
          : payload.membershipLimit;
      payload.billingBasis = payload.billingBasis || null;

      payload.allowAdditionalPetFromApp = payload.allowAdditionalPetFromApp !== false;
      payload.ageBlocksContracting = payload.ageBlocksContracting === true;
      // duracionMeses: asegurar entero ≥ 1 (default 12)
      payload.duracionMeses =
        payload.duracionMeses == null || payload.duracionMeses === '' || Number.isNaN(payload.duracionMeses)
          ? 12
          : Math.max(1, Math.trunc(payload.duracionMeses));

      payload.planes = (payload.planes || [])
        .filter(p => !!p.planId)
        .map(p => ({
          planId: p.planId,
          precioMensualNeto: p.precioMensualNeto,
          monedaPlan: p.monedaPlan,
          visible: p.visible !== false,
          type: p.type || null,
        }));

      payload.domains = (payload.domains || []).filter(d => !!d.domain?.trim());

      // TPA no tiene planes ni upsell. Filas sin plan elegido no se envían.
      payload.upsellPlans = this.isUnitBilled
        ? []
        : (payload.upsellPlans || [])
          .filter(u => !!u.planId)
          .map(u => ({
            planId:            u.planId,
            trigger:           u.trigger || 'additional_pet',
            type:              u.type || null,
            monedaPlan:        u.monedaPlan || this.monedaDerived || null,
            // Un campo vaciado llega como '' desde v-model.number: vacío es 0.
            precioMensualNeto: Number(u.precioMensualNeto) || 0,
          }));

      this.applyBillingTerms(payload);
      this.applyExtras(payload);

      // Los descuentos se aplican por método de pago. Generamos una fila por cada
      // método de pago distinto derivado del tipo de cobro de los planes,
      // compartiendo los mismos %.
      const planModes = [...new Set(
        payload.planes.map(p => PLAN_TYPE_TO_PAYMENT_MODE[p.type]).filter(Boolean)
      )];
      // Sin planes no hay método de pago al que colgar los descuentos: la fila de
      // paymentModes se genera por método, no por contrato.
      payload.paymentModes = planModes.length > 0
        ? planModes.map(m => ({
            paymentMode: m,
            monthlyDiscount: payload.descuentos.monthlyDiscount || 0,
            annualDiscount: payload.descuentos.annualDiscount || 0,
          }))
        : [];
      delete payload.descuentos;

      return payload;
    },

    /**
     * @param replaceCountryDefault - true solo al reintentar después de confirmar que se
     *   reemplaza el default del país. Se compara con `=== true` porque, desde el botón,
     *   el primer argumento es el evento del click.
     */
    async submit(replaceCountryDefault) {
      if (!this.$refs.form.validate()) {
        // El campo que falta puede estar en una pestaña cerrada: sin esto, Guardar
        // no hace nada visible y no hay forma de saber por qué.
        this.showValidation = true;
        const primera = this.tabsWithErrors[0];
        if (primera) this.activeTab = primera.value;
        return;
      }
      this.showValidation = false;
      this.saving = true;
      this.error  = null;

      const payload = this.buildPayload();
      if (replaceCountryDefault === true) payload.replaceCountryDefault = true;
      const res = this.editingId
        ? await ContratoService.update(this.editingId, payload)
        : await ContratoService.create(payload);

      if (res.status === 'ok') {
        await this.loadAll();
        this.closeDialog();
      } else if (res.code === 'COUNTRY_DEFAULT_EXISTS') {
        // Nada se guardó todavía: el backend valida antes de escribir.
        this.countryDefaultConflict = res.existingCodigo || 'otro contrato';
      } else {
        this.error = `Ocurrió un error al guardar el contrato.${res.message ? ' — ' + res.message : ''}`;
      }
      this.saving = false;
    },

    confirmReplaceCountryDefault() {
      this.countryDefaultConflict = null;
      this.submit(true);
    },

    planName(planId) {
      return (this.planes.find(p => p.id === planId) || {}).name || '';
    },

    planTypeLabel(value) {
      return PLAN_TYPE_ITEMS.find(t => t.value === value)?.text || value || '—';
    },

    billingBasisLabel(value) {
      return BILLING_BASIS_ITEMS.find(b => b.value === value)?.text || '—';
    },

    hasExtras(item) {
      return !!item && (
        item.collectionFeeAmount != null ||
        item.commissionAmount != null ||
        item.managementFeeAmount != null
      );
    },

    isExpired(endDate) {
      if (!endDate) return false;
      return new Date(endDate) < new Date();
    },

    openNps(item) {
      this.npsItem = item;
      this.npsDialog = true;
    },

    /** Refleja en la fila el NPS nuevo tras agregar, corregir o eliminar una calificación. */
    /** Actualiza el PDF de la fila sin recargar el listado. */
    onDocumentUpdated({ contratoId, document }) {
      const contrato = this.contratos.find(c => c.id === contratoId);
      if (contrato) this.$set(contrato, 'document', document);
    },

    onDocumentNotify({ type, message }) {
      this.documentSnackbar = { show: true, message, color: type === 'error' ? 'error' : 'success' };
    },

    onNpsUpdated(summary) {
      const contrato = this.contratos.find(c => c.id === this.npsItem.id);
      if (contrato) this.$set(contrato, 'npsSummary', summary);
    },

    /**
     * Orden de la tabla. Solo la columna NPS tiene orden propio: los "Sin NPS"
     * quedan siempre al final, en ambos sentidos. El resto usa el de Vuetify.
     */
    sortContratos(items, sortBy, sortDesc, locale) {
      if (sortBy[0] !== 'npsSummary') return sortItems(items, sortBy, sortDesc, locale);
      return items.sort((a, b) => compareNpsSummaries(a.npsSummary, b.npsSummary, sortDesc[0]));
    },

    lineaColor(linea) {
      const map = {
        'B2B Employee Benefit': 'blue darken-1',
        'B2B Asistencias':      'deep-purple darken-1',
        'B2B2C Pago Directo':   'teal darken-1',
        'B2B2C Upsell':         'cyan darken-1',
        'B2B2C Planilla':       'indigo darken-1',
        'B2C':                  'orange darken-1',
        'TPA':                  'blue-grey darken-1',
      };
      return map[linea] || 'grey darken-1';
    },
  },
};
</script>

<style scoped>
/* Alto fijo del dialog: no cambia de alto al cambiar de pestaña ni al cambiar la
   línea de negocio (que muestra u oculta secciones enteras), así el botón Guardar
   del resumen se queda siempre en el mismo lugar. El formulario y el resumen
   hacen scroll por separado dentro de ese alto. */
.contrato-dialog {
  height: 75vh;
  min-height: 560px;
  overflow: hidden;
}
.contrato-dialog__form {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
.contrato-tabs-panel {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}
/* Títulos de sección del formulario: mismo recuadro que el MRR estimado del resumen.
   El padding lateral pasa del v-card-text a cada pestaña porque v-window recorta
   (overflow: hidden) lo que sale de su caja. */
.contrato-tabs-panel {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.contrato-tabs-panel .v-window-item {
  padding: 0 24px;
}
.contrato-tabs-panel .section-label {
  /* La etiqueta de los campos outlined sobresale hacia arriba: hace falta más aire. */
  margin-bottom: 20px !important;
  /* Sobresale 12px a cada lado: el contenido queda dentro del ancho del título. */
  margin-left: -12px;
  margin-right: -12px;
  padding: 12px;
  border-radius: 10px;
  background: #1d3259;
  border: 1px solid #2f4a7a;
}
.contrato-tabs-panel .section-label .primary--text,
.contrato-tabs-panel .section-label .v-icon {
  color: #d7e0f0 !important;
  caret-color: #d7e0f0 !important;
}
.contrato-tabs-panel .section-label .v-divider {
  display: none;
}

/* Ficha de solo lectura: alto mínimo para que el dialog no salte entre pestañas. */
.contrato-view-panel {
  min-height: 260px;
  max-height: 55vh;
  overflow-y: auto;
}
.contrato-vigencia {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f3f6fc;
}
.contrato-vigencia__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--v-primary-base);
}
.contrato-vigencia__filled {
  height: 4px;
  flex-basis: 0;
  background: var(--v-primary-base);
}
.contrato-vigencia__today {
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border-radius: 50%;
  flex-shrink: 0;
  border: 3px solid var(--v-primary-base);
  background: #ffffff;
}
.contrato-vigencia__rest {
  height: 4px;
  flex-basis: 0;
  background: #c9d6ef;
}
.contrato-vigencia__rest--open {
  height: 0;
  background: none;
  border-top: 3px dashed #9fb5dd;
}

/* Panel de resumen: mismo azul oscuro del resumen del editor de planes. */
.contrato-summary {
  /* Vuetify redondea la esquina inferior izquierda del último hijo de un v-card;
     aquí esa esquina toca el formulario y debe ser recta. */
  border-bottom-left-radius: 0 !important;
  width: 380px;
  flex-shrink: 0;
  min-height: 0;
  overflow: hidden;
  background: #13233f;
  color: #ffffff;
}
/* Solo la información hace scroll; los botones quedan fijos abajo para que
   Guardar esté a la vista aunque el resumen sea más alto que el dialog. */
.contrato-summary__body {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  gap: 16px;
  padding: 24px;
}
.contrato-summary__footer {
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid #2f4a7a;
}
.contrato-summary__eyebrow,
.contrato-summary__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a9b8d6;
}
.contrato-summary__label--warn {
  color: #f5a3b8;
}
.contrato-summary__rule {
  height: 1px;
  background: #2f4a7a;
}
.contrato-summary__company {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
}
.contrato-summary__muted {
  font-size: 13px;
  color: #c3cee3;
}
.contrato-summary__chip {
  font-size: 12px;
  font-weight: 700;
  color: #d7e0f0;
  background: #2a4170;
  padding: 3px 10px;
  border-radius: 999px;
}
.contrato-summary__mrr {
  padding: 16px;
  border-radius: 10px;
  background: #1d3259;
  border: 1px solid #2f4a7a;
}
.contrato-summary__mrr-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
}
.contrato-summary__mrr-value--empty {
  color: #8fa0c0;
}
.contrato-summary__caption {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #c3cee3;
}
.contrato-summary__facts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
}
.contrato-summary__value {
  font-weight: 700;
  text-align: right;
}
.contrato-summary__value--warn,
.contrato-summary__missing {
  color: #f5a3b8;
}
.contrato-summary__missing {
  font-size: 14px;
}
.contrato-summary__ready {
  font-size: 14px;
  font-weight: 600;
  color: #9fe0bb;
}
.contrato-summary .contrato-summary__cancel {
  color: #c3cee3;
}

/* Preferencias de correo: filas separadas por una línea, y la que está suprimida
   se apaga sobre un fondo gris para que se lea de un vistazo cuál quedó cortada. */
.email-pref + .email-pref {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.email-pref--off {
  background: #fafafa;
}
</style>
